import {findDirectRoomByUserId, resolveOrCreateDirectRoom} from './directRoomLookup';
import {privateReplyExcerpt, privateReplyMediaExcerpt} from '../../utils/privateReplyQuote';
import {useOpenDirectChatFromState} from './openDirectChatFromState';
import type {OpenChatMessageLocationState} from '../../hooks/useOpenDirectChat';
import {resolveComposerIsRoomEncrypted} from './composerRoomEncryption';
import {AiBotPendingNotice} from "./AiBotPendingNotice";
import {saveRoomDetails} from "./roomDetails";
import {leaveDirectRoom} from "./leaveDirectRoom";
import {canLeaveDirectRoom} from "./canLeaveDirectRoom";
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {selectUserId, selectTenant} from '../../store/features/Auth/authSlice';
import {selectMatrixIdToUserId} from '../../store/features/IdResolver/idResolverSlice';
import {useRoomMessages, useTyping, useRoomActions} from '../../hooks/useMatrix';
import {useInitialRoomAutoSelect} from '../../hooks/useInitialRoomAutoSelect';
import {matrixService} from '../../services/matrixService';
import {getRoomKickForbiddenDescription, isRoomKickForbidden} from '../../services/roomKickError.ts';
import {getAiBotRoomErrorDescription} from '../../services/aiBotRoomError.ts';
import {
    useFetchUsersQuery,
    useLazyResolveMatrixIdsQuery,
    useLazyResolveUserIdsQuery,
    User,
    UserElement
} from "../../store/features/Users/userService.ts";
import MediaUpload from '../../components/Chat/MediaUpload';
import RoomList from '../../components/Chat/RoomList';
import CreateRoomModal, {UserWithMatrixId} from "../../components/Chat/CreateRoomModal.tsx";
import {polyfillCountryFlagEmojis} from "country-flag-emoji-polyfill";
import {PhoneHQSpinner, PhoneHQAvatar, PhoneHQButton, PhoneHQDialog, PhoneHQDialogFooter, PhoneHQDropdown, PhoneHQInput, StackedAvatars, PhoneHQTooltip} from 'ch3-ui-lib';
import {TelcoStatus, UserStatus} from "../../store/features/Team/teamService.ts";
import {getUserStatus, getUserStatusName} from "../../utilis/getStatus.ts";
import {
    ArrowDown,
    BookOpen,
    Clock,
    EllipsisVertical,
    Info,
    LockOpen,
    Mail,
    Plus,
    Trash2,
    UserIcon,
    X,
    Search,
    Bell,
    BellOff,
    LogOut,
    Bot,
    Image,
    Phone
} from 'lucide-react';
import MatrixMessageSearch from "../../components/Chat/MessageSearch.tsx";
import KnowledgeBasePanel from "../../components/Chat/KnowledgeBasePanel.tsx";
import {EventType, MatrixEvent, Room, RoomMember, RoomEvent} from "matrix-js-sdk";
import {getFullName} from "../../utilis/getFullName.ts";
import {hexToEmoji} from "../../components/Emoji/emoji.tsx";
import {LocalTime} from "../../components/LocalTime.tsx";
import {checkMeetingStatusExpiration, checkStatusExpiration} from "../../utilis/statusUtilis.ts";
import {ConversationStart} from "../../components/Chat/ConversationStart.tsx";
import {showToast} from "../../store/features/Notifications/notificationsSlice.tsx";
import googleCalendarIcon from "../../assets/google-calendar.svg";
import {convertFromISOFormat} from "../../utilis/timeUtilis.ts";
import {
    useGetUserProfileQuery,
    useLazyGetUserProfileQuery, UserSettings
} from "../../store/features/ProfileSettings/profileSettingsService.ts";
import UserListToCreateRoom from "../../components/Chat/UserListToCreatreRoom.tsx";
import {useMatrixContext} from "../../providers/MatrixProvider.tsx";
import ChatMessageInput from "../../components/Chat/ChatMessageInput.tsx";
import {ImageGallery} from "../../components/Chat/ImageGallery.tsx";
import {ImageInfo} from "../../components/Chat/chatTypes.ts";
import MessageGroupComponent from "../../components/Chat/MessageGroup.tsx";
import {useIdResolver} from "../../hooks/useIdResolver.ts";
import {EncryptedStatus} from "../../components/Chat/EncryptedStatus.tsx";
import {throttle} from 'lodash';
import {LeaveConversationDialog} from "../../components/Chat/LeaveConversationDialog.tsx";
import {InviteProgressDialog} from "../../components/Chat/InviteProgressDialog.tsx";
import {DuplicateGroupDialog} from "../../components/Chat/DuplicateGroupDialog.tsx";
import ChatSummaryPanel from "../../components/Chat/ChatSummaryPanel.tsx";
import {
    getAiBotRoomStatus,
    resolveAiBotJoinTimestamp,
    getBotUserId,
    isServiceParticipant,
    type MatrixRoomPowerLevels,
} from "../../utils/aiBotUtils.ts";
import {useIsAiBotEnabledQuery, useIsAiKnowledgeBaseEnabledQuery} from "../../services/chatService.ts";
import {extractMentionsFromFormattedBody} from "../../utils/mentionUtils.ts";
import {GroupAvatarTile, RoomAvatar} from "../../components/Chat/RoomAvatar.tsx";
import AvatarPicker from "../../components/Chat/AvatarPicker.tsx";
import type {ChatNotificationMuteDurationHours} from '../../services/chatNotificationMuteService.ts';
import {useAuthenticatedMedia} from "../../hooks/useAuthenticatedMedia.ts";
import {makeMatrixCall} from "../../services/matrixCallService.ts";
import {RootState, useAppDispatch} from "../../store/store.ts";
import {setCallFrom} from "../../store/features/calls/dialerSlice.ts";
import {CallStateEnum} from "../../components/Dialer/callState";
import {useRoomCallEntry} from "../../features/groupCalls/hooks/useRoomCallEntry.ts";
import {isOneToOneRoom, showsCallBadge} from "../../features/groupCalls/services/roomCallEntry.ts";
import {directCallButton} from "../../features/groupCalls/services/callButtonState.ts";
import {GroupRoomCallButtons} from "../../features/groupCalls/components/RoomCallButtons.tsx";
import {openDirectPrep, promptDndCall} from "../../features/callSurface/store/callSurfaceSlice";
import {CallInProgressBadge} from "../../features/groupCalls/components/CallInProgressBadge.tsx";
import {ChatCallBanner} from "../../features/callSurface/calls/ChatCallBanner";
import {blockCallDuringGroupCall} from "../../services/groupCallBusyGuard.ts";


export interface RoomUser {
    userId: string;
    displayName: string;
    avatarUrl?: string;
}

export interface UserDirectoryItem {
    avatar_url?: string;
    display_name?: string;
    user_id: string
}

const headerIconStatusBase = "absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white";

const headerUserStatusStyles: Record<TelcoStatus, string> = {
    [TelcoStatus.ONLINE]: `${headerIconStatusBase} bg-green-500`,
    [TelcoStatus.TALKING]: `${headerIconStatusBase} bg-yellow-500`,
    [TelcoStatus.OFFLINE]: `${headerIconStatusBase} bg-fill-subtle`,
    [TelcoStatus.UNKNOWN]: `${headerIconStatusBase} bg-fill-subtle`,
    [TelcoStatus.DND]: `${headerIconStatusBase} bg-red-500`,
};

const headerBotStatusStyles = {
    loading: `${headerIconStatusBase} bg-yellow-400 animate-pulse`,
    checking: `${headerIconStatusBase} bg-fill-subtle`,
    repairRequired: `${headerIconStatusBase} bg-amber-500`,
    active: `${headerIconStatusBase} bg-green-500`,
    inactive: `${headerIconStatusBase} bg-red-500`,
} as const;

const MAX_ATTACHMENTS_PER_MESSAGE = 10;

const EMPTY_MENTIONS: {userId: string; displayName: string}[] = [];
const EMPTY_ROOM_MENTIONS: {roomId: string; name: string}[] = [];

const getMemberStatusDisplayInfo = (user: UserStatus) => {
    let isCustomStatusExpired = true;
    let isMeetingStatusExpired = true;

    checkStatusExpiration(user, (expired) => {
        isCustomStatusExpired = expired;
    });
    checkMeetingStatusExpiration(user, (expired) => {
        isMeetingStatusExpired = expired;
    });

    return {
        customStatusName: !isCustomStatusExpired ? user.customUserStatus?.name : null,
        customStatusIcon: !isCustomStatusExpired ? user.customUserStatus?.icon : null,
        isInMeeting: !isMeetingStatusExpired && user.meetingUserStatus?.status === 'IN_MEETING',
    };
};

const ROOM_MUTE_DURATION_OPTIONS: Array<{
    label: string;
    value: ChatNotificationMuteDurationHours | null;
}> = [
    {label: 'For 1 hour', value: 1},
    {label: 'For 2 hours', value: 2},
    {label: 'For 4 hours', value: 4},
    {label: 'For 8 hours', value: 8},
    {label: 'For 24 hours', value: 24},
    {label: 'Until turned back on', value: null},
];

const RoomMuteButton: React.FC<{
    isMuted: boolean;
    onMute: (durationHours: ChatNotificationMuteDurationHours | null) => Promise<void>;
    onUnmute: () => Promise<void>;
}> = ({isMuted, onMute, onUnmute}) => {
    if (isMuted) {
        return (
            <PhoneHQButton
                className="w-full"
                variant="secondary"
                size="sm"
                onClick={() => void onUnmute()}
            >
                <Bell size={16}/>
                Unmute
            </PhoneHQButton>
        );
    }

    return (
        <PhoneHQDropdown
            align="start"
            side="bottom"
            sideOffset={4}
            trigger={
                <PhoneHQButton className="w-full" variant="secondary" size="sm">
                    <BellOff size={16}/>
                    Mute
                </PhoneHQButton>
            }
        >
            {ROOM_MUTE_DURATION_OPTIONS.map((option) => (
                <PhoneHQDropdown.Item
                    key={option.value ?? 'permanent'}
                    onClick={() => void onMute(option.value)}
                >
                    {option.label}
                </PhoneHQDropdown.Item>
            ))}
        </PhoneHQDropdown>
    );
};

const InfoPanel: React.FC<{
    room: Room | null | undefined;
    roomUsers: RoomUser[];
    roomAvatarUsers: RoomUser[];
    onClose: () => void;
    onChangeGroupName: (name: string) => Promise<void>;
    onChangeRoomAvatar: (file: File) => Promise<void>;
    inviteUserToRoom: (roomId: string, userId: string) => Promise<void>;
    onKickMember: (userId: string) => Promise<void>;
    currentUserId: string;
    users: UserElement[];
    usersWithStatuses: (RoomUser & UserStatus)[];
    usersIds: string[];
    selectedRoomId: string | null;
    userWithoutConversation?: UserSettings | null;
    getUserId: (matrixId: string) => string;
    isMuted: boolean;
    onMute: (durationHours: ChatNotificationMuteDurationHours | null) => Promise<void>;
    onUnmute: () => Promise<void>;
    onLeaveRoom: () => Promise<void>;
    onLeaveDirectRoom: () => Promise<void>;
    onSelectRoom: (roomId: string | null) => Promise<void>;
    isRoomDirect?: boolean;
    isAiBotInRoom: boolean;
    isAiBotEnabled: boolean;
    isEncrypted?: boolean;
    onEnableEncryption?: () => Promise<void>;
    profileUserId?: string | null;
    onProfileClose?: () => void;
}> = ({
          room,
          roomUsers,
          roomAvatarUsers,
          onClose,
          onKickMember,
          onChangeGroupName,
          onChangeRoomAvatar,
          inviteUserToRoom,
          currentUserId,
          users,
          usersWithStatuses,
          usersIds,
          userWithoutConversation,
          selectedRoomId,
          getUserId,
          isMuted,
          onMute,
          onUnmute,
          onLeaveRoom,
          onLeaveDirectRoom,
          onSelectRoom,
          isRoomDirect,
          isAiBotInRoom,
          isAiBotEnabled,
          isEncrypted,
          onEnableEncryption,
          profileUserId,
          onProfileClose
      }) => {


    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [groupName, setGroupName] = useState(room?.name || '');
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
    const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
    const [isLeavingRoom, setIsLeavingRoom] = useState(false);
    const [showAvatarPicker, setShowAvatarPicker] = useState(false);
    const [isAvatarPreviewOpen, setIsAvatarPreviewOpen] = useState(false);
    const [isEncryptionDialogOpen, setIsEncryptionDialogOpen] = useState(false);
    const [isSavingGroupDetails, setIsSavingGroupDetails] = useState(false);
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const roomAvatarMxcUrl = !isRoomDirect ? room?.getMxcAvatarUrl() || '' : '';
    const {blobUrl: resolvedRoomAvatarUrl} = useAuthenticatedMedia(roomAvatarMxcUrl);

    const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
    const canEditGroupName = Boolean(
        currentUserId && room?.currentState.maySendStateEvent(EventType.RoomName, currentUserId)
    );
    const canEditGroupAvatar = Boolean(
        currentUserId && room?.currentState.maySendStateEvent(EventType.RoomAvatar, currentUserId)
    );
    const canEditGroupDetails = canEditGroupName || canEditGroupAvatar;
    const creationEvent = room?.currentState.getStateEvents(EventType.RoomCreate, '');
    const creationContent = creationEvent?.getContent();
    const isProtectedMember = useCallback((userId: string) =>
        userId === currentUserId || userId === creationEvent?.getSender() || (
            creationContent?.room_version === '12' &&
            Array.isArray(creationContent.additional_creators) && creationContent.additional_creators.includes(userId)
        ), [currentUserId, creationEvent, creationContent]);

    const saveGroupDetails = useCallback(async () => {
        if (isSavingGroupDetails) return;
        setIsSavingGroupDetails(true);
        const result = await saveRoomDetails({
            saveName: canEditGroupName && groupName !== room?.name
                ? () => onChangeGroupName(groupName) : undefined,
            saveAvatar: canEditGroupAvatar && avatarFile
                ? () => onChangeRoomAvatar(avatarFile) : undefined,
        });
        setIsSavingGroupDetails(false);
        if (result.kind === 'saved') {
            setIsDialogOpen(false);
            return;
        }
        console.error('Failed to update group details:', result.error);
        dispatch(showToast({
            title: 'Could not update group details',
            description: result.nameSaved
                ? 'The group name was saved, but the avatar could not be saved. Please retry the avatar.'
                : 'Could not confirm your changes. Refresh the room before retrying.',
            type: 'error',
        }));
    }, [
        avatarFile,
        canEditGroupAvatar,
        canEditGroupName,
        dispatch,
        groupName,
        isSavingGroupDetails,
        onChangeGroupName,
        onChangeRoomAvatar,
        room?.name,
    ]);
    const [selectedUser, setSelectedUser] = useState<UserSettings | null>(null);
    const [viewingProfile, setViewingProfile] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const profileRequestIdRef = useRef(0);

    const [fetchUser] = useLazyGetUserProfileQuery();
    const [resolveMatrixIds] = useLazyResolveMatrixIdsQuery();
    const requestedAvatarMatrixIdsRef = useRef<Set<string>>(new Set());

    const userAvatarByAppId = useMemo(() => {
        const avatars = new Map<string, string>();

        users.forEach((user: UserElement) => {
            if (!user.avatarUrl) return;

            avatars.set(String(user.id), user.avatarUrl);
            avatars.set(String(user.userId), user.avatarUrl);
        });

        return avatars;
    }, [users]);

    const getMemberAvatarUrl = useCallback((matrixId: string): string | undefined => {
        const appUserId = getUserId(matrixId);
        return appUserId ? userAvatarByAppId.get(String(appUserId)) : undefined;
    }, [getUserId, userAvatarByAppId]);

    useEffect(() => {
        const unresolvedMatrixIds = roomUsers
            .map(user => user.userId)
            .filter(matrixId => !getUserId(matrixId) && !requestedAvatarMatrixIdsRef.current.has(matrixId));

        if (!unresolvedMatrixIds.length) return;

        unresolvedMatrixIds.forEach(matrixId => requestedAvatarMatrixIdsRef.current.add(matrixId));
        resolveMatrixIds({matrixIds: unresolvedMatrixIds});
    }, [roomUsers, getUserId, resolveMatrixIds]);

    const status = useMemo(() => {
        if (!selectedUser || !usersWithStatuses?.length) return usersWithStatuses?.[0];
        return usersWithStatuses.find(u => String(getUserId(String(u.userId))) === String(selectedUser.userId)) || usersWithStatuses[0];
    }, [selectedUser?.userId, usersWithStatuses, getUserId]);

    const [isExpired, setIsExpired] = useState(true);
    const [isMeetingExpired, setIsMeetingExpired] = useState(true);

    const {
        dateString: meetingDateString,
        timeString: meetingTimeString
    } = convertFromISOFormat(status?.meetingUserStatus?.dateTo || '');
    const meetingExpires = meetingDateString ? `${meetingDateString}, ${meetingTimeString}` : meetingTimeString;

    const roomId = room?.roomId;
    const roomUsersKey = useMemo(() =>
            roomUsers?.map(user => user?.userId).join(','),
        [roomUsers]
    );

    useEffect(() => {
        checkStatusExpiration(status, setIsExpired);
        checkMeetingStatusExpiration(status, setIsMeetingExpired);
        const interval = setInterval(() => {
            checkStatusExpiration(status, setIsExpired);
        }, 5 * 1000);

        return () => clearInterval(interval);
    }, [status?.customUserStatus?.dueDate]);

    useEffect(() => {
        setSelectedUser(null);
        setIsLoading(true);

        const isDM = isRoomDirect ?? false;
        setViewingProfile(isDM);

        if (userWithoutConversation) {
            setViewingProfile(true);
            setSelectedUser(userWithoutConversation);
            setIsLoading(false);
            return;
        }

        if (isDM && roomUsers.length === 1) {
            const userId = roomUsers[0]?.userId;

            if (userId) {
                const phqUserId = getUserId(String(userId));
                if (phqUserId) {
                    fetchUser(phqUserId)
                        .unwrap()
                        .then(user => {
                            setSelectedUser(user);
                            setIsLoading(false);
                        })
                        .catch(error => {
                            setIsLoading(false);
                            console.error('Failed to fetch user:', error);
                        });
                } else {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        } else {
            setIsLoading(false);
        }

    }, [selectedRoomId, userWithoutConversation, roomUsersKey, roomId, getUserId, isRoomDirect]);


    const statusStyles = {
        ONLINE: "bg-green-500 w-2.5 h-2.5",
        TALKING: "bg-yellow-500 w-2.5 h-2.5",
        OFFLINE: "bg-fill-subtle w-2.5 h-2.5",
        DND: "bg-red-500 w-2.5 h-2.5",
        UNKNOWN: "bg-fill-subtle w-2.5 h-2.5"
    }

    const handleAddMembers = useCallback(async (matrixIds: string[]) => {
        const roomId = room?.roomId;
        if (!roomId) return;

        const invitePromises = matrixIds.map(async (matrixId) => {
            try {
                await inviteUserToRoom(roomId, matrixId);
            } catch (error) {
                console.error('Failed to invite user:', error);
            }
        });

        await Promise.all(invitePromises);
        setShowAddMemberDialog(false);
    }, [room?.roomId, inviteUserToRoom]);

    const viewProfile = useCallback(async (userId: string) => {
        const requestId = ++profileRequestIdRef.current;
        setIsLoading(true);
        try {
            let phqUserId = getUserId(String(userId));

            if (!phqUserId) {
                const resolved = await resolveMatrixIds({matrixIds: [userId]}).unwrap();
                phqUserId = resolved[userId] || '';
            }

            if (phqUserId) {
                const user = await fetchUser(phqUserId).unwrap();
                if (profileRequestIdRef.current === requestId) {
                    setSelectedUser(user);
                    setViewingProfile(true);
                }
            }
        } catch (error) {
            console.error('Failed to fetch user:', error);
        } finally {
            if (profileRequestIdRef.current === requestId) {
                setIsLoading(false);
            }
        }
    }, [fetchUser, getUserId, resolveMatrixIds]);

    useEffect(() => {
        if (profileUserId) {
            void viewProfile(profileUserId);
            return;
        }

        if (!isRoomDirect && !userWithoutConversation) {
            profileRequestIdRef.current += 1;
            setViewingProfile(false);
            setSelectedUser(null);
        }
    }, [profileUserId, viewProfile, isRoomDirect, userWithoutConversation]);

    const handleCloseProfile = useCallback(() => {
        profileRequestIdRef.current += 1;
        onProfileClose?.();
        if (!isRoomDirect && !userWithoutConversation) {
            setViewingProfile(false);
            setSelectedUser(null);
        } else {
            onClose();
        }
    }, [isRoomDirect, onClose, onProfileClose, userWithoutConversation]);

    const returnProfileView = useCallback(() => {
        if (!selectedUser) return null;

        return (
            <div
                className={`${isLoading ? 'opacity-0 overflow-hidden' : 'opacity-100'}  transition-all duration-300 ease-in-out w-80 bg-surface border-l border-line flex flex-col h-full`}>
                {/* Header */}
                <div className="pl-5 py-4 pr-3 border-b border-line flex items-center justify-between h-[72px]">
                    <h3 className="text-base font-semibold text-ink">Profile</h3>
                    <button
                        onClick={handleCloseProfile}
                        className="text-ink-muted hover:text-ink-secondary transition-colors"
                    >
                        <X className="button p-1.5" size={28}/>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {/* User Profile Section */}
                    <div className="p-5 border-b border-line  ">
                        <div className="flex items-center justify-center mb-2">
                            <div className="flex w-full min-w-0 flex-col items-center space-y-10">
                                <div className={' w-full flex justify-center'}>
                                    <PhoneHQAvatar
                                        name={selectedUser.firstName + ' ' + selectedUser.lastName}
                                        src={selectedUser?.avatarUrl}
                                        size={'300px'}
                                        status={getUserStatus(selectedUser)}
                                    />

                                </div>
                                <div>
                                    <div className="font-semibold text-ink text-xl">
                                        {getFullName(selectedUser.firstName, selectedUser.lastName)}
                                    </div>
                                    <div className={'text-base text-ink-muted text-ellipsis'}>
                                        {selectedUser.position}
                                    </div>
                                </div>
                                <div className="w-full min-w-0 space-y-3">
                                    <div>
                                        <div
                                            className="mx-[2px] text-tiny font-medium text-ink-muted text-ellipsis flex items-center gap-4">
                                          <span>
                                              <span
                                                  className={statusStyles[getUserStatus(status) as TelcoStatus] + ' block rounded-full'}></span>
                                          </span>
                                            {getUserStatusName(status)}
                                        </div>
                                    </div>
                                    {(!isExpired && status?.customUserStatus?.icon) &&
                                        <PhoneHQTooltip content={status.customUserStatus.name || 'No status description'} side="top"><div className="flex w-full min-w-0 gap-4 rounded">
                                                <div className="flex min-w-0 items-center w-full">
                                                    <div className="mr-3 flex w-4 shrink-0 items-center justify-center">
                                                        {hexToEmoji(status.customUserStatus.icon, '')}
                                                    </div>
                                                    <span className="block min-w-0 flex-1 truncate text-sm">
                                                        {status.customUserStatus.name || 'No status description'}
                                                    </span>
                                                </div>
                                            </div></PhoneHQTooltip>
                                    }
                                    {Boolean(status?.meetingUserStatus?.status === 'IN_MEETING') &&
                                        <div className="flex items-center w-full mt-4 rounded hover:cursor-pointer">
                                            <div className="flex items-center">
                                                <div className="w-5 h-5 mr-3">
                                                    <img className='' src={googleCalendarIcon}/>
                                                </div>
                                                <div className="flex items-center gap-1 text-tiny w-full break-normal">
                                                    <span>In a meeting</span>
                                                    <div className="text-xs text-ink-muted">
                                                        {meetingExpires ? `until ${meetingExpires}` : ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    {selectedUser.userTimeZone && <div className={'flex gap-4'}>
                                        <Clock size={16}/>
                                        <LocalTime className={'font-medium text-ink'}
                                                   timezone={selectedUser.userTimeZone}/>
                                    </div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Members Section */}
                    <div className="p-5">
                        <div className="flex items-center justify-between  mb-5 ">
                            <h5 className="font-semibold text-ink text-sm">Contact information</h5>
                        </div>

                        <div className="space-y-5 text-ink-muted">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <Mail size={20}/>
                                    <div>
                                        <p className="text-xxs ">
                                            Email
                                        </p>
                                        <a
                                            target='_blank'
                                            href={`mailto:${selectedUser.email}`}
                                            className="text-brand-ink font-semibold text-tiny cursor-pointer mr-2"
                                        >{selectedUser.email}</a>
                                    </div>

                                </div>

                            </div>

                        </div>
                    </div>

                    {canLeaveDirectRoom({
                        isRoomDirect,
                        roomUsersCount: roomUsers.length,
                        selectedRoomId,
                        userWithoutConversation,
                        viewingProfile,
                    }) && (
                        <div className={'flex p-4 gap-4 justify-center'}>
                            <PhoneHQButton
                                className={'w-full text-red-600 hover:text-red-700'}
                                variant="secondary"
                                size={'sm'}
                                disabled={isLeavingRoom}
                                data-testid="chat.leave-dm.trigger"
                                onClick={() => setIsLeaveDialogOpen(true)}>
                                <LogOut size={16}/>
                                {intl.formatMessage({id: 'chat.leave-dm.action', defaultMessage: 'Leave chat'})}
                            </PhoneHQButton>

                            <LeaveConversationDialog
                                open={isLeaveDialogOpen}
                                onOpenChange={setIsLeaveDialogOpen}
                                isDirect
                                onConfirm={async () => {
                                    setIsLeavingRoom(true);
                                    try {
                                        await leaveDirectRoom({
                                            leaveRoom: onLeaveDirectRoom,
                                            onLeft: async () => {
                                                await onSelectRoom(null);
                                                onClose();
                                            },
                                            onLeaveFailed: (error) => {
                                                console.error('Failed to leave chat:', error);
                                                dispatch(showToast({
                                                    title: intl.formatMessage({id: 'chat.leave-dm.failed', defaultMessage: 'Failed to leave chat'}),
                                                    description: error instanceof Error ? error.message : undefined,
                                                    type: 'error',
                                                }));
                                            },
                                        });
                                    } finally {
                                        setIsLeavingRoom(false);
                                    }
                                }}
                            />
                        </div>
                    )}

                    {/* AI Summary Section */}
                    {isAiBotEnabled && selectedRoomId && (
                        <ChatSummaryPanel roomId={selectedRoomId} currentUserId={currentUserId} isAiBotInRoom={isAiBotInRoom} />
                    )}

                </div>
                {showAddMemberDialog &&
                    <CreateRoomModal
                        isEditing={true}
                        onRoomCreate={handleAddMembers}

                        users={users.filter(u => !usersIds.includes(String(u.id)))}
                        onClose={() => setShowAddMemberDialog(false)}
                    />}
            </div>
        );
    }, [selectedUser, isLoading, handleCloseProfile, status, statusStyles, isExpired, isMeetingExpired, meetingExpires, showAddMemberDialog, handleAddMembers, users, usersIds, selectedRoomId, currentUserId, isAiBotInRoom, isAiBotEnabled, intl, isLeaveDialogOpen, isLeavingRoom, onLeaveDirectRoom, onSelectRoom, onClose, dispatch, isRoomDirect, roomUsers, userWithoutConversation, viewingProfile]);

    const returnGroupView = useCallback(() => {
        return (
            <div
                className={`${isLoading ? 'opacity-0 overflow-hidden' : 'opacity-100'} transition-all duration-300 ease-in-out w-80 bg-surface border-l border-line flex flex-col h-full`}>
                {/* Header */}
                <div className="pl-5 py-4 pr-3 border-b border-line flex items-center justify-between h-[72px]">
                    <h3 className="text-base font-semibold text-ink">{isRoomDirect ? 'Chat' : 'Group'}</h3>
                    <button
                        onClick={onClose}
                        className="text-ink-muted hover:text-ink-secondary transition-colors"
                    >
                        <X className="button p-1.5" size={28}/>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {/* Group Name Section */}
                    <div className="p-4 border-b border-line">
                        <div className="flex items-center justify-center mb-2">
                            <div className={`items-center space-x-3 ${room?.getMxcAvatarUrl() ? 'space-y-3' : 'space-y-10'}`}>
                                <div className="w-full flex justify-center">
                                    {room?.getMxcAvatarUrl() ? (
                                        <button
                                            type="button"
                                            aria-label="View group avatar"
                                            className="rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
                                            onClick={() => setIsAvatarPreviewOpen(true)}
                                            disabled={!resolvedRoomAvatarUrl}
                                        >
                                            <RoomAvatar
                                                room={room}
                                                roomUsers={roomAvatarUsers}
                                                size="64px"
                                                resolvedAvatarUrl={resolvedRoomAvatarUrl}
                                                className="cursor-zoom-in"
                                            />
                                        </button>
                                    ) : (
                                        <RoomAvatar room={room} roomUsers={roomAvatarUsers} size="40px" />
                                    )}
                                </div>
                                <div>
                                    <div className="font-semibold text-ink text-base">
                                        {room?.currentState?.getStateEvents('m.room.name', '')?.getContent()?.name
                                            || roomUsers?.map(u => u.displayName).join(', ')
                                            || room?.name}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {!isRoomDirect && (
                            <div className='flex justify-center'>
                                <PhoneHQDialog
                                    noPadding
                                    open={isDialogOpen}
                                    onOpenChange={(open) => {
                                        setIsDialogOpen(open);
                                        if (open) {
                                            setGroupName(room?.name || '');
                                            setAvatarFile(null);
                                            setAvatarPreview(null);
                                            setShowAvatarPicker(false);
                                        }
                                    }}
                                    trigger={<button
                                        className="text-sm font-medium text-brand-ink hover:text-brand-ink transition-colors"
                                        disabled={!canEditGroupDetails}
                                        data-tooltip={!canEditGroupDetails ? 'Room permissions do not allow editing group details' : undefined}
                                    >
                                        Edit
                                    </button>
                                    }
                                    title={showAvatarPicker ? undefined : "Edit group details"} theme="light" close={!showAvatarPicker}>
                                    {showAvatarPicker ? (
                                        <AvatarPicker
                                            onSave={(file) => {
                                                setAvatarFile(file);
                                                setAvatarPreview(URL.createObjectURL(file));
                                                setShowAvatarPicker(false);
                                            }}
                                            onCancel={() => setShowAvatarPicker(false)}
                                        />
                                    ) : (
                                        <div className={'p-6'}>
                                            <div className="flex flex-col items-center mb-4">
                                                <div className="flex items-center justify-center mb-2">
                                                    {avatarPreview ? (
                                                        <div className="size-16 rounded-2xl overflow-hidden">
                                                            <img src={avatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
                                                        </div>
                                                    ) : (
                                                        <div className={'mb-4 mr-4'}>
                                                            <RoomAvatar room={room} roomUsers={roomAvatarUsers} size={room?.getMxcAvatarUrl() ? '64px' : '40px'} resolvedAvatarUrl={resolvedRoomAvatarUrl} />
                                                        </div>
                                                    )}
                                                </div>
                                                <PhoneHQButton
                                                    variant='tertiary'
                                                    className={'flex gap-1 py-0 h-7'}
                                                    disabled={!canEditGroupAvatar || isSavingGroupDetails}
                                                    onClick={() => setShowAvatarPicker(true)}
                                                >
                                                    <Image size={12} className={'!p-0'} color={'#3f3f46'}/>
                                                    Change avatar
                                                </PhoneHQButton>
                                            </div>
                                            <PhoneHQInput
                                                label={'Group name'}
                                                value={groupName}
                                                disabled={!canEditGroupName || isSavingGroupDetails}
                                                onChange={(e) => {
                                                    setGroupName(e.target.value)
                                                }} placeholder="Enter group name..."/>
                                            <PhoneHQDialogFooter>
                                                <PhoneHQButton variant='tertiary' className='mt-4 mr-2' onClick={() => {
                                                    setIsDialogOpen(false);
                                                }}>Cancel</PhoneHQButton>
                                                <PhoneHQButton
                                                    className='mt-4'
                                                    disabled={isSavingGroupDetails || !canEditGroupDetails}
                                                    onClick={() => void saveGroupDetails()}
                                                >
                                                    {isSavingGroupDetails ? 'Saving…' : 'Save'}
                                                </PhoneHQButton>
                                            </PhoneHQDialogFooter>
                                        </div>
                                    )}
                                </PhoneHQDialog>

                            </div>

                        )}
                        <div className={'flex justify-center'}>
                            <EncryptedStatus isEncrypted={isEncrypted ?? false}/>
                        </div>


                    </div>
                    <div className={'flex p-4 gap-4 justify-center'}>
                            <RoomMuteButton isMuted={isMuted} onMute={onMute} onUnmute={onUnmute}/>
                        <PhoneHQButton
                            className={'w-full'}
                            variant="secondary"
                            size={'sm'}
                            disabled={isLeavingRoom}
                            onClick={() => setIsLeaveDialogOpen(true)}>
                            <LogOut size={16}/>
                            Leave
                        </PhoneHQButton>

                        <LeaveConversationDialog
                            open={isLeaveDialogOpen}
                            onOpenChange={setIsLeaveDialogOpen}
                            onConfirm={async () => {
                                setIsLeavingRoom(true);
                                try {
                                    await onLeaveRoom();
                                    await onSelectRoom(null);
                                    onClose();
                                } finally {
                                    setIsLeavingRoom(false);
                                }
                            }}
                        />
                    </div>
                    {/* E2E Section */}
                    {!isEncrypted && (<div className="p-5 border-b border-line bg-surface-muted">

                        <div>
                            <div className="flex items-start gap-3">
                                <LockOpen size={16} className="text-ink-muted mt-0.5 shrink-0"/>
                                <div>
                                    <p className="text-sm font-semibold text-ink text-tiny">This chat is not
                                        end-to-end encrypted</p>
                                    <p className="text-sm text-ink-muted mt-1 text-tiny">
                                        Messages are visible to the platform and can be searched by all members. The
                                        full archive is accessible to everyone, including new participants.
                                    </p>
                                </div>
                            </div>
                            {onEnableEncryption && (
                                <>
                                    <PhoneHQButton
                                        variant="secondary"
                                        size="sm"
                                        className="mt-3"
                                        onClick={() => setIsEncryptionDialogOpen(true)}
                                    >
                                        Enable end-to-end encrypted
                                    </PhoneHQButton>
                                    <PhoneHQDialog
                                        open={isEncryptionDialogOpen}
                                        onOpenChange={setIsEncryptionDialogOpen}
                                        theme="light"
                                        title={<h3 className="text-base font-semibold text-ink">Enable end-to-end
                                            encryption</h3>
                                        }
                                        close={true}
                                    >
                                        <div className="space-y-4">
                                            <p className="text-sm text-ink-muted-strong">
                                                Once enabled, all future messages in this chat will be secured and
                                                accessible only to group members.
                                            </p>
                                            <div className="text-sm text-ink-muted-strong">
                                                <p className="mb-2">Please note:</p>
                                                <ul className="list-disc pl-5 space-y-1">
                                                    <li>Searching messages in this chat will no longer be possible.</li>
                                                    <li>The archive of previous messages may not be available to new
                                                        members joining later.
                                                    </li>
                                                    <li>Encryption cannot be disabled once it's turned on.</li>
                                                </ul>
                                            </div>
                                            <p className="text-sm text-ink-muted-strong">
                                                By enabling encryption, you confirm that you understand and accept these
                                                limitations.
                                            </p>
                                            <PhoneHQDialogFooter>
                                                <PhoneHQButton
                                                    variant="tertiary"
                                                    onClick={() => setIsEncryptionDialogOpen(false)}
                                                >
                                                    Cancel
                                                </PhoneHQButton>
                                                <PhoneHQButton
                                                    onClick={async () => {
                                                        await onEnableEncryption();
                                                        setIsEncryptionDialogOpen(false);
                                                    }}
                                                >
                                                    Enable encryption
                                                </PhoneHQButton>
                                            </PhoneHQDialogFooter>
                                        </div>
                                    </PhoneHQDialog>
                                </>
                            )}
                        </div>

                    </div>)}
                    {/* Members Section */}
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <h5 className="font-semibold text-ink text-sm">Members</h5>

                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <PhoneHQButton size={'sm'} className={'px-0.5 h-8'} variant={'tertiary'}
                                                   onClick={() => {
                                                       setShowAddMemberDialog(true)
                                                   }}>
                                        <Plus size={16}/>
                                    </PhoneHQButton>

                                    <div>
                                        <p className="font-medium text-ink text-sm">
                                            Add members
                                        </p>
                                    </div>

                                </div>

                            </div>
                            {usersWithStatuses?.map((user) => {
                                const memberStatus = getMemberStatusDisplayInfo(user);

                                return (
                                    <div key={user.userId} className="flex items-center justify-between gap-2">
                                        <button
                                            type="button"
                                            className="flex items-center space-x-3 text-left"
                                            onClick={() => viewProfile(user.userId)}
                                            data-tooltip-right="View profile"
                                            aria-label={`View ${user.displayName}'s profile`}
                                        >
                                            <div className="flex min-w-0 flex-1 items-center space-x-3">
                                                <PhoneHQAvatar
                                                    name={user.displayName}
                                                    src={user.avatarUrl || getMemberAvatarUrl(String(user.userId))}
                                                    status={getUserStatus(user)}
                                                    size={'32px'}
                                                />
                                                <div className="min-w-0">
                                                    <p className="truncate font-medium text-ink text-sm">
                                                        {user.displayName}
                                                    </p>
                                                    {memberStatus.customStatusName && (
                                                        <div className="flex max-w-[190px] items-center gap-1 text-xs text-ink-muted">
                                                            {memberStatus.customStatusIcon && (
                                                                <div className="flex w-4 shrink-0 items-center justify-center">
                                                                    {hexToEmoji(memberStatus.customStatusIcon, '!w-3 !h-3')}
                                                                </div>
                                                            )}
                                                            <span className="truncate">{memberStatus.customStatusName}</span>
                                                        </div>
                                                    )}
                                                    {memberStatus.isInMeeting && (
                                                        <div className="flex items-center gap-1 text-xs text-ink-muted">
                                                            <img
                                                                className="h-3.5 w-3.5 shrink-0"
                                                                src={googleCalendarIcon}
                                                                alt=""
                                                            />
                                                            <span>In a meeting</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                        <PhoneHQDropdown
                                            align="end"
                                            side="bottom"
                                            showCloseIcon={true}
                                            trigger={
                                                <button
                                                    aria-label="More options"
                                                >
                                                    <EllipsisVertical className="button p-1.5" size={28}/>
                                                </button>
                                            }
                                        >
                                            <PhoneHQDropdown.Item onClick={() => viewProfile(user.userId)}>
                                                <UserIcon className="mr-3 h-4 w-4"/>
                                                View profile
                                            </PhoneHQDropdown.Item>
                                            <PhoneHQDropdown.Item
                                                disabled={isProtectedMember(String(user.userId))}
                                                onClick={() => void onKickMember(String(user.userId))}
                                            >
                                                <Trash2 className="mr-3 h-4 w-4"/>
                                                Remove from group
                                            </PhoneHQDropdown.Item>
                                        </PhoneHQDropdown>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* AI Summary Section */}
                    {isAiBotEnabled && selectedRoomId && (
                        <ChatSummaryPanel roomId={selectedRoomId} currentUserId={currentUserId} isAiBotInRoom={isAiBotInRoom} />
                    )}

                </div>
                {showAddMemberDialog &&
                    <CreateRoomModal
                        isEditing={true}
                        onRoomCreate={handleAddMembers}

                        users={users.filter(u => !usersIds.includes(String(u.id)))}
                        onClose={() => setShowAddMemberDialog(false)}
                    />}
                {room?.getMxcAvatarUrl() && (
                    <PhoneHQDialog
                        open={isAvatarPreviewOpen}
                        onOpenChange={setIsAvatarPreviewOpen}
                        title="Group avatar"
                        theme="light"
                    >
                        <div className="flex max-h-[70vh] justify-center overflow-hidden">
                            <img
                                src={resolvedRoomAvatarUrl || undefined}
                                alt={`${room?.name || 'Group'} avatar`}
                                className="size-[300px] max-h-[70vh] max-w-full rounded-2xl object-contain"
                            />
                        </div>
                    </PhoneHQDialog>
                )}
            </div>
        );
    }, [isLoading, onClose, room, roomUsers, roomAvatarUsers, isDialogOpen, isLeaveDialogOpen, isLeavingRoom, groupName, avatarFile, onChangeGroupName, onChangeRoomAvatar, setIsLeaveDialogOpen, showAddMemberDialog, usersWithStatuses, viewProfile, onKickMember, handleAddMembers, users, usersIds, selectedRoomId, currentUserId, isRoomDirect, isAiBotInRoom, isAiBotEnabled, showAvatarPicker, avatarPreview, isEncryptionDialogOpen, isEncrypted, onEnableEncryption, isMuted, onMute, onUnmute, onLeaveRoom, onSelectRoom, isAvatarPreviewOpen, canEditGroupDetails, canEditGroupName, canEditGroupAvatar, isSavingGroupDetails, saveGroupDetails, isProtectedMember]);

    return (
        // A DM the other person left has no profile to show; the room view
        // still lets the user leave it or invite someone back.
        ((isRoomDirect && roomUsers.length > 0) || (viewingProfile && selectedUser)) ? returnProfileView() : returnGroupView()
    );
};


const Chat: React.FC = () => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const location = useLocation();
    const navigate = useNavigate();

    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const {data: isAiBotEnabled} = useIsAiBotEnabledQuery();
    const {data: isAiKnowledgeBaseEnabled} = useIsAiKnowledgeBaseEnabledQuery();
    const [showMediaUpload, setShowMediaUpload] = useState(false);
    const [showCreateRoom, setShowCreateRoom] = useState(false);
    const [duplicateGroupRoomId, setDuplicateGroupRoomId] = useState<string | null>(null);
    const [duplicateGroupIsNamed, setDuplicateGroupIsNamed] = useState(false);
    const [inviteProgress, setInviteProgress] = useState<{
        invited: number;
        total: number;
        error?: boolean;
    } | null>(null);
    const [showInfoPanel, setShowInfoPanel] = useState(false);
    const [profileUserIdToView, setProfileUserIdToView] = useState<string | null>(null);
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
    const [userWithoutConversation, setUserWithoutConversation] = useState<{
        user: UserSettings;
        matrixId: string;
    } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [userProfile, setUserProfile] = useState<any>(null);
    const [diffrentTimeZone, setDiffrentTimeZone] = useState<boolean>(false)
    const [prepareNewChat, setPrepareNewChat] = useState<boolean>(false);
    const [isCreatingGroup, setIsCreatingGroup] = useState<boolean>(false);
    const [usersToCreateChat, setUsersToCreateChat] = useState<UserWithMatrixId[]>([])
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isTabVisible, setIsTabVisible] = useState(!document.hidden && document.hasFocus());
    const [optionalGroupName, setOptionalGroupName] = useState<string>('')
    const [pendingLocalRoomId, setPendingLocalRoomId] = useState<string | null>(null);
    const [isGroupNameDialogOpen, setIsGroupNameDialogOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [showSearchPanel, setShowSearchPanel] = useState(false);
    const [showKnowledgeBasePanel, setShowKnowledgeBasePanel] = useState(false);
    const [messageSearchQuery, setMessageSearchQuery] = useState('');
    const [isHeaderStatusExpired, setIsHeaderStatusExpired] = useState(true);
    const [isHeaderMeetingExpired, setIsHeaderMeetingExpired] = useState(true);

    // Image Gallery State
    const [showImageGallery, setShowImageGallery] = useState(false);
    const [galleryImages, setGalleryImages] = useState<ImageInfo[]>([]);
    const [galleryStartIndex, setGalleryStartIndex] = useState(0);

    const tenantId = useSelector(selectTenant);
    const currentPHQUserId = useSelector(selectUserId);
    const tenant = useSelector(selectTenant);
    const matrixIdToUserIdMap = useSelector(selectMatrixIdToUserId);

    const [fetchUser] = useLazyGetUserProfileQuery();
    const [resolveRoomMatrixIds] = useLazyResolveMatrixIdsQuery();
    const [resolveUserIds] = useLazyResolveUserIdsQuery();
    const {data: profileSettingsData} = useGetUserProfileQuery(currentPHQUserId, {skip: !currentPHQUserId});

    const [replyToMessage, setReplyToMessage] = useState<string | null>(null);
    const [privateReply, setPrivateReply] = useState<{
        targetRoomId: string;
        senderName: string;
        excerpt: string;
    } | null>(null);
    const [pendingPrivateReply, setPendingPrivateReply] = useState<{
        targetRoomId: string;
        senderName: string;
        excerpt: string;
    } | null>(null);
    const [editingMessage, setEditingMessage] = useState<{
        eventId: string;
        body: string;
        mentions: {userId: string; displayName: string}[];
        roomMentions: {roomId: string; name: string}[];
    } | null>(null);
    const [inputHeight, setInputHeight] = useState(80);
    const [callBannerHeight, setCallBannerHeight] = useState(0);

    const [permissionRequested, setPermissionRequested] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollTimeoutRef = useRef<number>();
    const loadMoreRef = useRef<() => void>(() => {
    });
    const hasMoreRef = useRef<boolean>(true);
    const isLoadingOlderRef = useRef<boolean>(false);
    const savedScrollTopRef = useRef<number | null>(null);


    const callState = useSelector((state: RootState) => state.dialer.callState);

    const {
        isInitialized,
        isAuthenticated,
        initialRoomRestoreSettled,
        sessionLockState,
        selectedRoomId,
        rooms,
        roomsWithLastMessages,
        notificationPermission,
        sendMessage,
        sendReplyMessage,
        sendPrivateReplyMessage,
        sendEditMessage,
        sendReaction,
        removeReaction,
        createRoom,
        selectRoom,
        sendFile,
        getClient,
        getCurrentUserId,
        requestNotificationPermission,
        markMessagesAsRead,
        changeRoomName,
        changeRoomAvatar,
        kickUserFromRoom,
        inviteUserToRoom,
        loadMoreRooms,
        hasMoreRooms,
        isRoomEncrypted,
        enableRoomEncryption,
        requestSessionTakeover
    } = useMatrixContext();

    const { getUserId, getMatrixId, allStatuses: statuses } = useIdResolver();

    const selectedRoomIdRef = useRef(selectedRoomId);
    useEffect(() => {
        selectedRoomIdRef.current = selectedRoomId;
    }, [selectedRoomId]);

    const openDirectChatStartRoomIdRef = useRef<string | null>(null);
    const canApplyOpenDirectChatResult = useCallback(() => {
        if (!isMountedRef.current) return false;
        return selectedRoomIdRef.current === openDirectChatStartRoomIdRef.current;
    }, []);

    const pendingOpenDirectUserId = (location.state as {openDirectWithUserId?: string} | null)
        ?.openDirectWithUserId;
    const consumeOpenDirectChatState = useCallback(() => {
        navigate(location.pathname, {replace: true, state: null});
    }, [navigate, location.pathname]);
    const resolveOpenDirectChatMatrixId = useCallback(async (appUserId: string) => {
        openDirectChatStartRoomIdRef.current = selectedRoomIdRef.current;
        matrixService.markExplicitRoomSelectionPending();
        const cached = getMatrixId(appUserId);
        if (cached) return cached;
        const resolved = await resolveUserIds({userIds: [appUserId]}).unwrap();
        return resolved[appUserId];
    }, [getMatrixId, resolveUserIds]);
    const lookupOpenDirectChatRoom = useCallback((matrixId: string) =>
        matrixService.findDirectMessageRoomOutcome([matrixId]), []);
    const waitForOpenDirectChatRoom = useCallback((matrixId: string) =>
        matrixService.waitForDirectMessageRoom([matrixId]), []);
    const findOpenDirectChatLocalRoom = useCallback((appUserId: string) => findDirectRoomByUserId({
        rooms: roomsWithLastMessages,
        targetUserId: appUserId,
        matrixIdToUserId: matrixIdToUserIdMap,
        isDirectRoom: room => matrixService.isDirectMessageRoom(room),
    }), [roomsWithLastMessages, matrixIdToUserIdMap]);
    const createOpenDirectChatRoom = useCallback((matrixId: string) => createRoom({
        invite: [matrixId],
        isDirectMessage: true,
    }), [createRoom]);
    const selectOpenDirectChatRoom = useCallback(async (roomId: string) => {
        if (!canApplyOpenDirectChatResult()) return;
        await selectRoom(roomId);
    }, [canApplyOpenDirectChatResult, selectRoom]);
    const reportOpenDirectChatError = useCallback(() => {
        if (!canApplyOpenDirectChatResult()) return;
        dispatch(showToast({
            title: intl.formatMessage({id: 'chat.openDirect.error.title'}),
            description: intl.formatMessage({id: 'chat.openDirect.error.description'}),
            type: 'error',
        }));
    }, [canApplyOpenDirectChatResult, dispatch, intl]);

    const isExplicitSelectionPending = useCallback(() => matrixService.isExplicitRoomSelectionPending(), []);
    useInitialRoomAutoSelect({
        isReady: isInitialized && isAuthenticated,
        restoreSettled: initialRoomRestoreSettled,
        selectedRoomId,
        hasPendingDeepLink: Boolean(pendingOpenDirectUserId),
        isExplicitSelectionPending,
        rooms: roomsWithLastMessages,
        selectRoom,
    });

    useOpenDirectChatFromState({
        pendingUserId: pendingOpenDirectUserId,
        isReady: isInitialized && isAuthenticated,
        consumeLocationState: consumeOpenDirectChatState,
        resolveMatrixId: resolveOpenDirectChatMatrixId,
        lookup: lookupOpenDirectChatRoom,
        waitForOutcome: waitForOpenDirectChatRoom,
        findLocalDirectRoom: findOpenDirectChatLocalRoom,
        createDirectRoom: createOpenDirectChatRoom,
        selectRoom: selectOpenDirectChatRoom,
        onError: reportOpenDirectChatError,
    });

    polyfillCountryFlagEmojis();

    const {
        groupedMessages,
        // Derived from the room's own `m.call.*` events, so a row follows its
        // call through its life: the invite's row becomes "Missed call" the
        // moment the hangup lands, and history paginated in later gets its
        // rows without anything being written to the room.
        callEntries,
        loading: isLoadingMessages,
        loadMoreMessages,
        isLoadingMore,
        hasMore
    } = useRoomMessages(selectedRoomId);
    const typingUsers = useTyping(selectedRoomId);
    const {isMuted, muteRoom, unmuteRoom, leaveRoom} = useRoomActions(selectedRoomId);
    const {data: users = []} = useFetchUsersQuery({});

    const userAvatarById = useMemo(() => {
        const avatars = new Map<string, string>();

        users.forEach((user: UserElement) => {
            if (!user.avatarUrl) return;

            avatars.set(String(user.id), user.avatarUrl);
            avatars.set(String(user.userId), user.avatarUrl);
        });

        return avatars;
    }, [users]);

    useEffect(() => {
        const updateVisibility = () => {
            const visible = !document.hidden && document.hasFocus();
            setIsTabVisible(visible);
        };
        document.addEventListener('visibilitychange', updateVisibility);
        window.addEventListener('focus', updateVisibility);
        window.addEventListener('blur', updateVisibility);
        return () => {
            document.removeEventListener('visibilitychange', updateVisibility);
            window.removeEventListener('focus', updateVisibility);
            window.removeEventListener('blur', updateVisibility);
        };
    }, []);

    useEffect(() => {
        hasMoreRef.current = hasMore;
        loadMoreRef.current = () => {
            if (!isLoadingMore && !isLoadingOlderRef.current && hasMore && messagesContainerRef.current) {
                savedScrollTopRef.current = messagesContainerRef.current.scrollTop;
                isLoadingOlderRef.current = true;
                loadMoreMessages();
            }
        };
    }, [loadMoreMessages, isLoadingMore, hasMore]);

    const scrollToBottom = useCallback(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, []);

    const handleMediaLoad = useCallback(() => {
        if (!messagesContainerRef.current) return;

        if (!hasScrolledForRoomRef.current || wasAtBottomRef.current) {
            requestAnimationFrame(() => {
                if (!messagesContainerRef.current || isLoadingOlderRef.current) return;
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            });
        }
    }, []);

    const cleanupFunctionsRef = useRef<(() => void)[]>([]);

    const messagesContainerCallbackRef = useCallback((container: HTMLDivElement | null) => {
        // Clean up previous event listeners
        cleanupFunctionsRef.current.forEach(cleanup => cleanup());
        cleanupFunctionsRef.current = [];

        if (!container) {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            return;
        }

        let isUserScrolling = false;
        let userScrollTimeout: number | null = null;
        let loadMoreTimeout: number | null = null;
        let lastScrollTop = container.scrollTop;

        const checkIfAtBottom = () => {
            const {scrollTop, scrollHeight, clientHeight} = container;
            const threshold = 100;
            const isAtBottom = Math.abs(scrollTop) < threshold;
            setShowScrollButton(!isAtBottom);

            if (!isLoadingOlderRef.current) {
                wasAtBottomRef.current = isAtBottom;
            }

            const isNearTop = Math.abs(scrollTop) >= scrollHeight - clientHeight - 200;
            if (isNearTop && hasMoreRef.current) {
                if (loadMoreTimeout) {
                    clearTimeout(loadMoreTimeout);
                }
                loadMoreTimeout = window.setTimeout(() => {
                    loadMoreRef.current();
                }, 150);
            }

            const scrollDelta = Math.abs(scrollTop - lastScrollTop);
            lastScrollTop = scrollTop;

            if (scrollDelta > 5) {
                isUserScrolling = true;
                container.classList.add('scrolling');

                if (userScrollTimeout) {
                    clearTimeout(userScrollTimeout);
                }
                userScrollTimeout = window.setTimeout(() => {
                    isUserScrolling = false;
                    container.classList.remove('scrolling');
                }, 1000);
            }

            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = window.setTimeout(() => {
                if (!isUserScrolling) {
                    container.classList.remove('scrolling');
                }
            }, 1000);
        };

        const handleMouseEnter = () => {
            container.classList.add('hovered');
        };

        const handleMouseLeave = () => {
            container.classList.remove('hovered');
        };

        container.addEventListener('scroll', checkIfAtBottom, {passive: true});
        container.addEventListener('mouseenter', handleMouseEnter);
        container.addEventListener('mouseleave', handleMouseLeave);

        checkIfAtBottom();

        cleanupFunctionsRef.current.push(
            () => container.removeEventListener('scroll', checkIfAtBottom),
            () => container.removeEventListener('mouseenter', handleMouseEnter),
            () => container.removeEventListener('mouseleave', handleMouseLeave),
            () => {
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }
                if (loadMoreTimeout) {
                    clearTimeout(loadMoreTimeout);
                }
            }
        );
    }, []);

    const setMessagesContainerRef = useCallback((container: HTMLDivElement | null) => {
        messagesContainerCallbackRef(container);
        messagesContainerRef.current = container;
    }, [messagesContainerCallbackRef]);

    const sendTypingNotification = useMemo(() =>
            throttle((roomId) => {
                getClient()?.sendTyping(roomId, true, 3000).catch(console.error);
            }, 5000),
        []);

    const prepareRoomEncryption = useMemo(() =>
            throttle((roomId: string) => {
                matrixService.prepareToEncrypt(roomId);
            }, 60000),
        []);
    useEffect(() => {
        if (isAuthenticated && !permissionRequested && notificationPermission === 'default') {
            const timer = setTimeout(async () => {
                try {
                    await requestNotificationPermission();
                    setPermissionRequested(true);
                } catch (error) {
                    console.error('Failed to request notification permission:', error);
                }
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [isAuthenticated, notificationPermission, permissionRequested, requestNotificationPermission]);


    const isNearBottom = useCallback(() => {
        if (!messagesContainerRef.current) return false;
        const {scrollTop} = messagesContainerRef.current;
        const threshold = 100;
        return Math.abs(scrollTop) < threshold;
    }, []);

    const hasScrolledForRoomRef = useRef<boolean>(false);
    const prevMessageCountRef = useRef<number>(0);
    const prevLastMessageIdRef = useRef<string | null>(null);
    const [isMessagesReady, setIsMessagesReady] = useState(false);
    const wasAtBottomRef = useRef<boolean>(true);


    useLayoutEffect(() => {
        hasScrolledForRoomRef.current = false;
        hasMoreRef.current = true;
        wasAtBottomRef.current = true;
    }, [selectedRoomId]);

    useLayoutEffect(() => {
        if (selectedRoomId && !hasScrolledForRoomRef.current && messagesContainerRef.current) {
            if (groupedMessages.length > 0 || !isLoadingMessages) {
                const container = messagesContainerRef.current;

                requestAnimationFrame(() => {
                    container.scrollTop = container.scrollHeight;
                    requestAnimationFrame(() => {
                        container.scrollTop = container.scrollHeight;
                    });
                });

                hasScrolledForRoomRef.current = true;
                setIsMessagesReady(true);
            }
        }
    }, [selectedRoomId, groupedMessages, isLoadingMessages]);

    useLayoutEffect(() => {
        if (!isLoadingOlderRef.current || isLoadingMore) return;

        const c = messagesContainerRef.current;
        if (c && savedScrollTopRef.current !== null) {
            // Restore scroll position - in column-reverse, the browser may clamp scrollTop
            // when content is hidden (system messages), causing a jump toward the bottom.
            c.scrollTop = savedScrollTopRef.current;
            savedScrollTopRef.current = null;
        }

        requestAnimationFrame(() => {
            isLoadingOlderRef.current = false;
        });
    }, [groupedMessages, isLoadingMore]);


    useEffect(() => {
        if (!selectedRoomId || groupedMessages.length === 0 || !hasScrolledForRoomRef.current) {
            return;
        }

        const totalMessages = groupedMessages.reduce((sum, group) => sum + group.messages.length, 0);
        const lastGroup = groupedMessages[groupedMessages.length - 1];
        const lastMessage = lastGroup?.messages[lastGroup.messages.length - 1];
        const lastMessageId = lastMessage?.getId() || null;

        // When loading older messages, still update counters but skip scroll logic
        if (isLoadingOlderRef.current) {
            prevMessageCountRef.current = totalMessages;
            prevLastMessageIdRef.current = lastMessageId;
            return;
        }

        const isNewMessageReceived = lastMessageId !== prevLastMessageIdRef.current && prevLastMessageIdRef.current !== null;

        if (totalMessages > prevMessageCountRef.current && prevMessageCountRef.current > 0 && isNewMessageReceived) {
            const isOwnMessage = lastMessage?.getSender() === getCurrentUserId();

            if (isOwnMessage) {
                const timer = setTimeout(scrollToBottom, 0);
                prevMessageCountRef.current = totalMessages;
                prevLastMessageIdRef.current = lastMessageId;
                return () => clearTimeout(timer);
            } else if (wasAtBottomRef.current) {
                const timer = setTimeout(scrollToBottom, 50);
                prevMessageCountRef.current = totalMessages;
                prevLastMessageIdRef.current = lastMessageId;
                return () => clearTimeout(timer);
            }
        }

        prevMessageCountRef.current = totalMessages;
        prevLastMessageIdRef.current = lastMessageId;
    }, [selectedRoomId, groupedMessages, scrollToBottom, isNearBottom, getCurrentUserId]);

    useEffect(() => {
        if (selectedRoomId && groupedMessages.length > 0 && isTabVisible) {
            const timer = setTimeout(async () => {
                console.log('[readReceipt:timeout]', { hidden: document.hidden, hasFocus: document.hasFocus(), isNearBottom: isNearBottom(), time: Date.now() });
                // Re-check actual tab visibility at execution time.
                // The React state (isTabVisible) may be stale if the browser didn't fire
                // visibilitychange reliably (observed in Firefox with multiple tabs/sessions).
                if (document.hidden) return;

                if (!isNearBottom()) return;

                const lastGroup = groupedMessages[groupedMessages.length - 1];
                if (lastGroup && lastGroup.messages.length > 0) {
                    const lastMessage = lastGroup.messages[lastGroup.messages.length - 1];
                    const eventId = lastMessage.getId();
                    if (!selectedRoomId) return;
                    if (eventId) {
                        await markMessagesAsRead(selectedRoomId, eventId);
                    } else {
                        await markMessagesAsRead(selectedRoomId);
                    }
                }
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [selectedRoomId, groupedMessages, markMessagesAsRead, isTabVisible, isNearBottom]);

    useEffect(() => {
        if (selectedRoomId && messagesEndRef.current && isTabVisible) {
            const observer = new IntersectionObserver(
                async (entries) => {
                    entries.forEach(async (entry) => {
                        console.log('[readReceipt:observer]', { isIntersecting: entry.isIntersecting, ratio: entry.intersectionRatio, hidden: document.hidden, time: Date.now() });
                        if (entry.isIntersecting && entry.intersectionRatio >= 0.9 && !document.hidden) {
                            if (groupedMessages.length > 0) {
                                const lastGroup = groupedMessages[groupedMessages.length - 1];
                                if (lastGroup.messages.length > 0) {
                                    const lastMessage = lastGroup.messages[lastGroup.messages.length - 1];
                                    const eventId = lastMessage.getId();
                                    if (eventId) {
                                        await markMessagesAsRead(selectedRoomId, eventId);
                                    }
                                }
                            }
                        }
                    });
                },
                {
                    threshold: [0.9],
                    rootMargin: '0px 0px -10px 0px'
                }
            );

            observer.observe(messagesEndRef.current);
            return () => observer.disconnect();
        }
    }, [selectedRoomId, groupedMessages, markMessagesAsRead, isTabVisible]);

    useEffect(() => {
        setEditingMessage(null);
        setReplyToMessage(null);
        setPrivateReply(null);
    }, [selectedRoomId]);

    useEffect(() => {
        if (!pendingPrivateReply || pendingPrivateReply.targetRoomId !== selectedRoomId) {
            return;
        }
        setEditingMessage(null);
        setReplyToMessage(null);
        setPrivateReply(pendingPrivateReply);
        setPendingPrivateReply(null);
    }, [selectedRoomId, pendingPrivateReply]);

    const handleReply = useCallback((messageId: string) => {
        setEditingMessage(null);
        setPrivateReply(null);
        setReplyToMessage(messageId);
        setShowMediaUpload(false);
    }, []);

    const handleEdit = useCallback((messageId: string) => {
        if (!selectedRoomId) return;
        const target = matrixService.getRoom(selectedRoomId)?.findEventById(messageId);
        if (!target) return;

        const content = target.getContent();
        const body = content?.body;
        if (typeof body !== 'string') return;

        const {mentions, roomMentions} = extractMentionsFromFormattedBody(
            content?.formatted_body,
            content?.['m.mentions']?.user_ids
        );

        setReplyToMessage(null);
        setPrivateReply(null);
        setShowMediaUpload(false);
        setEditingMessage({eventId: messageId, body, mentions, roomMentions});
    }, [selectedRoomId]);

    const handleSelectRoom = async (roomId: string) => {
        setEditingMessage(null);
        setReplyToMessage(null);
        setPrivateReply(null);
        pendingRoomCreationRef.current = null;
        setUserWithoutConversation(null);
        setProfileUserIdToView(null);
        setDiffrentTimeZone(false);
        setSearchTerm('');
        await selectRoom(roomId);
        setShowCreateRoom(false);
        setPrepareNewChat(false);
        setIsCreatingGroup(false);
        setUsersToCreateChat([]);
        if (pendingLocalRoomId) {
            matrixService.removeLocalRoom(pendingLocalRoomId);
            setPendingLocalRoomId(null);
        }
    };

    const roomsWithLastMessagesRef = useRef(roomsWithLastMessages);
    const matrixIdToUserIdMapRef = useRef(matrixIdToUserIdMap);
    const handleSelectRoomRef = useRef(handleSelectRoom);
    const createRoomRef = useRef(createRoom);
    const intlRef = useRef(intl);
    const dispatchRef = useRef(dispatch);
    useEffect(() => {
        roomsWithLastMessagesRef.current = roomsWithLastMessages;
        matrixIdToUserIdMapRef.current = matrixIdToUserIdMap;
        handleSelectRoomRef.current = handleSelectRoom;
        createRoomRef.current = createRoom;
        intlRef.current = intl;
        dispatchRef.current = dispatch;
    });

    const handlePrivateReply = useCallback(async (message: MatrixEvent) => {
        const senderId = message.getSender()?.trim();
        const sourceRoomId = selectedRoomIdRef.current;
        if (!senderId || !sourceRoomId) return;

        const room = matrixService.getRoom(sourceRoomId);
        const senderName = room?.getMember(senderId)?.rawDisplayName || senderId;
        const content = message.getContent();
        const rawExcerpt = privateReplyMediaExcerpt(content) ?? (content.body || '');

        const canApplyResult = () => isMountedRef.current && selectedRoomIdRef.current === sourceRoomId;

        matrixService.markExplicitRoomSelectionPending();

        try {
            const targetRoomId = await resolveOrCreateDirectRoom({
                peerId: senderId,
                lookup: () => matrixService.findDirectMessageRoomOutcome([senderId]),
                waitForOutcome: () => matrixService.waitForDirectMessageRoom([senderId]),
                findLocalFallback: () => {
                    const appUserId = matrixIdToUserIdMapRef.current[senderId];
                    if (!appUserId) return undefined;
                    return findDirectRoomByUserId({
                        rooms: roomsWithLastMessagesRef.current,
                        targetUserId: appUserId,
                        matrixIdToUserId: matrixIdToUserIdMapRef.current,
                        isDirectRoom: candidateRoom => matrixService.isDirectMessageRoom(candidateRoom),
                    });
                },
                createRoom: () => createRoomRef.current({invite: [senderId], isDirectMessage: true}),
            });

            if (!canApplyResult()) return;

            await handleSelectRoomRef.current(targetRoomId);
            setPendingPrivateReply({
                targetRoomId,
                senderName,
                excerpt: privateReplyExcerpt(rawExcerpt),
            });
        } catch (error) {
            console.error('Failed to open the DM for a private reply:', error);
            if (!canApplyResult()) return;
            dispatchRef.current(showToast({
                title: intlRef.current.formatMessage({id: 'chat.private-reply.open-failed.title', defaultMessage: "Couldn't open the conversation"}),
                description: intlRef.current.formatMessage({id: 'chat.private-reply.open-failed.description', defaultMessage: 'Try again in a moment'}),
                type: 'error',
            }));
        }
    }, []);

    const handleFileUpload = async (file: File) => {
        if (!selectedRoomId) return;

        try {
            await sendFile(selectedRoomId, file, replyToMessage || undefined);
        } catch (error) {
            console.error('Failed to send file:', error);
        }
    };

    // Deduplicates concurrent room creations. The create-branch in onSendMessage is
    // guarded by React state (selectedRoomId, usersToCreateChat) that only updates on
    // re-render, so rapid sends — or a double-clicked Create button — would otherwise
    // each create their own room. All callers in that window share this promise and
    // land in the same room.
    const pendingRoomCreationRef = useRef<Promise<string> | null>(null);

    useEffect(() => {
        // Once the created room is actually selected, the create-branch is unreachable
        // and the pending promise must not leak into the next conversation.
        if (selectedRoomId) {
            pendingRoomCreationRef.current = null;
        }
    }, [selectedRoomId]);

    const handleCreateRoom = async (matrixIds: string[], roomName?: string): Promise<string> => {
        const pending = pendingRoomCreationRef.current;
        if (pending) {
            return pending;
        }

        const creation = createRoomWithLocalEcho(matrixIds, roomName);
        pendingRoomCreationRef.current = creation;
        creation.catch(() => {
            pendingRoomCreationRef.current = null;
        });
        return creation;
    };

    const createRoomWithLocalEcho = async (matrixIds: string[], roomName?: string) => {
        const localOtherUsers = userWithoutConversation
            ? [{
                userId: userWithoutConversation.matrixId,
                displayName: `${userWithoutConversation.user.firstName} ${userWithoutConversation.user.lastName}`.trim(),
                avatarUrl: userWithoutConversation.user.avatarUrl || ''
            }]
            : usersToCreateChat.map(u => ({
                userId: u.matrixId,
                displayName: `${u.firstName} ${u.lastName}`.trim(),
                avatarUrl: u.avatarUrl || ''
            }));

        // Matrix has no room type — a DM is only a room the client lists in
        // m.direct — so the user's choice decides, not the head count. Picking
        // "New Group" with a single person must produce a group: it is the
        // ordinary way to start a group that later has three, and inviting
        // someone changes nothing about the room. startPreparingChat already
        // recorded that choice in isCreatingGroup; without consulting it here,
        // a one-person group silently became a direct chat.
        const isDirectMessage = !isCreatingGroup && !roomName && matrixIds.length === 1;

        const localRoomId = pendingLocalRoomId || matrixService.createLocalRoom({
            name: roomName,
            otherUsers: localOtherUsers,
            isDirect: isDirectMessage,
        });
        if (pendingLocalRoomId) setPendingLocalRoomId(null);

        const totalInvites = matrixIds.length;
        // Up to 10 invites ride in the createRoom call (near-instant); beyond that
        // they trickle in via MultiInviter, so show a progress dialog for the wait.
        const showInviteProgress = totalInvites > 10;

        try {
            setShowCreateRoom(false);

            if (showInviteProgress) {
                setInviteProgress({ invited: 0, total: totalInvites });
            }

            const roomId = await createRoom({
                name: roomName,
                invite: matrixIds,
                isDirectMessage,
            }, showInviteProgress
                ? (invited, total) => setInviteProgress({ invited, total })
                : undefined);

            // Map local room to real room so the dedup filter hides the real room by ID
            matrixService.mapLocalToRealRoom(localRoomId, roomId);

            setUserWithoutConversation(null);

            if (showInviteProgress) {
                setInviteProgress(null);
            }

            await selectRoom(roomId);
            return roomId;

        } catch (error) {
            console.error('Failed to create room:', error);
            setShowCreateRoom(false);
            matrixService.removeLocalRoom(localRoomId);
            if (showInviteProgress) {
                setInviteProgress(prev => ({
                    invited: prev?.invited ?? 0,
                    total: prev?.total ?? totalInvites,
                    error: true,
                }));
            } else {
                dispatch(showToast({
                    title: 'Failed to create conversation',
                    description: 'Please try again.',
                    type: 'error',
                }));
            }
            throw error;
        }
    }

    const currentUserId = matrixService.getCurrentUserMatrixId();
    const currentRoom = rooms.find(r => r.roomId === selectedRoomId);

    // Track membership changes to invalidate roomUsers memo
    const [membershipVersion, setMembershipVersion] = useState(0);

    useEffect(() => {
        if (!currentRoom) return;

        const handleTimelineEvent = (event: any) => {
            // Only invalidate when membership events occur (join, leave, kick, ban, invite)
            if (event.getType() === 'm.room.member') {
                setMembershipVersion(v => v + 1);
            }
        };

        currentRoom.on(RoomEvent.Timeline, handleTimelineEvent);

        return () => {
            currentRoom.off(RoomEvent.Timeline, handleTimelineEvent);
        };
    }, [currentRoom, selectedRoomId]);

    useEffect(() => {
        if (!currentRoom) return;

        const unresolvedMatrixIds = currentRoom.getJoinedMembers()
            .map((member: RoomMember) => member.userId)
            .filter((matrixId: string) => !Object.prototype.hasOwnProperty.call(matrixIdToUserIdMap, matrixId));

        if (unresolvedMatrixIds.length) {
            resolveRoomMatrixIds({matrixIds: unresolvedMatrixIds});
        }
    }, [currentRoom, matrixIdToUserIdMap, membershipVersion, resolveRoomMatrixIds]);

    const getAppUserAvatarUrl = useCallback((matrixId: string): string | undefined => {
        const appUserId = matrixIdToUserIdMap[matrixId];
        return appUserId ? userAvatarById.get(String(appUserId)) : undefined;
    }, [matrixIdToUserIdMap, userAvatarById]);

    const roomUsers: RoomUser[] = useMemo(() =>
        currentRoom?.getJoinedMembers()
            .filter((member: RoomMember) => member.userId !== currentUserId && !isServiceParticipant(member.userId))
            .map((member: RoomMember): RoomUser => ({
                userId: member.userId,
                displayName: member.rawDisplayName || member.userId,
                avatarUrl: member.getAvatarUrl(window.__ENV__?.SYNAPSE_BASE_URL, 64, 64, 'crop', true, true) || undefined
            })) || []
    , [currentRoom, currentUserId, membershipVersion]);

    const roomAvatarUsers: RoomUser[] = useMemo(() => {
        const joinedUsers = currentRoom?.getJoinedMembers()
            .filter((member: RoomMember) => member.membership === 'join' && !isServiceParticipant(member.userId))
            .map((member: RoomMember): RoomUser => ({
                userId: member.userId,
                displayName: member.rawDisplayName || member.userId,
                avatarUrl: member.getAvatarUrl(window.__ENV__?.SYNAPSE_BASE_URL, 64, 64, 'crop', true, true) || undefined
            })) || [];

        if (!joinedUsers.length) return roomUsers;
        if (!currentUserId) return joinedUsers;

        const me = joinedUsers.find(user => user.userId === currentUserId);
        const others = joinedUsers.filter(user => user.userId !== currentUserId);
        return me ? [me, ...others] : joinedUsers;
    }, [currentRoom, currentUserId, getAppUserAvatarUrl, roomUsers, membershipVersion]);

    const typingMembers = useMemo(() =>
        typingUsers
            .filter(userId => !isServiceParticipant(userId))
            .map(userId => {
                const member = currentRoom?.getMember(userId);
                const rawName = member?.rawDisplayName || member?.name || userId;
                const displayName = rawName.replace(/\s*\(@[^)]+\)\s*$/, '');

                return {
                    userId,
                    displayName,
                    avatarUrl: member?.getAvatarUrl(window.__ENV__?.SYNAPSE_BASE_URL, 24, 24, 'crop', true, true) || undefined,
                };
            })
    , [currentRoom, typingUsers]);

    const typingIndicatorText = useMemo(() => {
        const names = typingMembers.map(member => member.displayName);

        if (names.length === 1) {
            return `${names[0]} is typing...`;
        }

        if (names.length === 2) {
            return `${names[0]} and ${names[1]} are typing...`;
        }

        return `${names[0]}, ${names[1]}, and ${names.length - 2} others are typing...`;
    }, [typingMembers]);

    // A bot is an auxiliary room member and does not turn a direct chat into a
    // group. Matrix records the user's room choice in m.direct/is_direct; room
    // membership can change independently of that choice.
    const isCurrentRoomDirect = currentRoom
        ? matrixService.isDirectMessageRoom(currentRoom)
        : false;

    const roomCallEntry = useRoomCallEntry({
        roomId: currentRoom?.roomId,
        isOneToOne: currentRoom
            ? isOneToOneRoom({
                isDirect: isCurrentRoomDirect,
                joinedHumanMembers: currentRoom.getJoinedMembers()
                    .filter(member => !isServiceParticipant(member.userId)).length,
            })
            : true,
        legacyDirectAvailable: roomUsers.length <= 1 && !!userProfile,
        sessionReady: isInitialized,
        tabOwner: sessionLockState === 'active',
        busy: callState !== CallStateEnum.IDLE,
    });

    const openInfoPanel = useCallback((profileUserId: string | null = null) => {
        setProfileUserIdToView(profileUserId);
        setShowSearchPanel(false);
        setShowKnowledgeBasePanel(false);
        setShowInfoPanel(true);
    }, []);

    const viewChatUser = useCallback((matrixUserId: string) => {
        openInfoPanel(isCurrentRoomDirect ? null : matrixUserId);
    }, [isCurrentRoomDirect, openInfoPanel]);

    // A #room mention is a jump link: it should land the user in that
    // conversation, not open the info panel for the room they are already in.
    // What "#" offers: the conversations already on screen, newest first, named
    // and joined. A draft room has no id to link to yet, and the room you are
    // writing in is filtered out by the composer.
    const roomMentionTargets = useMemo(() => roomsWithLastMessages
        .filter(entry => !entry.isLocal && entry.roomId)
        .map(entry => ({
            roomId: entry.roomId,
            name: entry.roomName || entry.room?.name || '',
        }))
        .filter(entry => !!entry.name && !entry.name.includes('chat.chte.ch')),
    [roomsWithLastMessages]);

    const reportUnreachableMention = useCallback((description: string) => {
        dispatch(showToast({
            type: 'error',
            title: 'Conversation unavailable',
            description,
        }));
    }, [dispatch]);

    const openRoomMention = useCallback(async (roomIdOrAlias: string) => {
        const roomId = await matrixService.resolveRoomId(roomIdOrAlias);
        if (!roomId) {
            reportUnreachableMention('This chat could not be found.');
            return;
        }

        await selectRoom(roomId);

        // selectRoom accepts an id it cannot find, so without this check a
        // mention of a chat the reader has no access to would look exactly like
        // a click that did nothing.
        if (!matrixService.getRoom(roomId)) {
            reportUnreachableMention('You do not have access to this chat.');
        }
    }, [reportUnreachableMention, selectRoom]);

    const reportUnresolvedMention = useCallback(() => {
        reportUnreachableMention('This link could not be opened.');
    }, [reportUnreachableMention]);

    const usersIds = useMemo(() => roomUsers?.map(user => getUserId(user.userId)), [roomUsers, getUserId]);

    /**
     * Ring the peer back from a call row in the timeline — the same call the
     * header's phone button places. Group rooms and rooms whose peer is not
     * resolved yet get no handler, and the row then draws as history only.
     */
    const callBackPeer = useCallback(() => {
        if (blockCallDuringGroupCall(callState, dispatch)) return;
        if (!selectedRoomId || callState !== CallStateEnum.IDLE) return;
        if (roomUsers.length > 1 || !userProfile) return;

        const peerStatus = getUserStatus(
            statuses?.find(s => String(s.userId) === getUserId(String(roomUsers[0]?.userId)))
        ) as TelcoStatus | undefined;
        if (peerStatus === TelcoStatus.TALKING || peerStatus === TelcoStatus.OFFLINE) return;

        const userToCall = {
            userId: userProfile?.userId || '',
            firstName: userProfile?.firstName || '',
            lastName: userProfile?.lastName || '',
            avatarUrl: userProfile?.avatarUrl || '',
        } as User;

        if (peerStatus === TelcoStatus.DND) {
            dispatch(setCallFrom(userToCall));
            dispatch(promptDndCall({roomId: selectedRoomId}));
            return;
        }

        makeMatrixCall(userToCall, getUserId(currentUserId), tenantId, dispatch, selectedRoomId);
    }, [selectedRoomId, callState, roomUsers, userProfile, statuses, getUserId, currentUserId, tenantId, dispatch]);

    const canCallBack = roomUsers.length <= 1 && !!userProfile;

    // AiBot AI indicator state
    const [isInvitingBot, setIsInvitingBot] = useState(false);
    const [isRepairingBot, setIsRepairingBot] = useState(false);
    const [isKickingBot, setIsKickingBot] = useState(false);
    const [isKickBotConfirmOpen, setIsKickBotConfirmOpen] = useState(false);
    const [isRefreshingBot, setIsRefreshingBot] = useState(false);
    const [botActionError, setBotActionError] = useState<{roomId: string; message: string} | null>(null);

    const aiBotUserId = useMemo(() => {
        if (!currentUserId) return null;
        return getBotUserId(currentUserId);
    }, [currentUserId]);

    const aiBotMember = currentRoom && aiBotUserId
        ? currentRoom.getMember(aiBotUserId)
        : null;
    const isAiBotInRoom = aiBotMember?.membership === 'join';

    const botMembershipEvent = currentRoom && aiBotUserId
        ? currentRoom.currentState.getStateEvents('m.room.member', aiBotUserId) : null;
    const botMembershipEventId = botMembershipEvent?.getId();
    const [resolvedBotJoin, setResolvedBotJoin] = useState<{eventId: string; timestamp: number | null}>();
    const aiBotJoinedAt = isAiBotInRoom && resolvedBotJoin && resolvedBotJoin.eventId === botMembershipEventId
        ? resolvedBotJoin.timestamp : null;
    useEffect(() => {
        if (!currentRoom || !aiBotUserId || !isAiBotInRoom || !botMembershipEvent || !botMembershipEventId) return;
        const client = matrixService.getClient();
        if (!client) return;
        let cancelled = false;
        const revision = (event: MatrixEvent) => ({
            eventId: event.getId() ?? '', timestamp: event.getTs(),
            membership: event.getContent().membership,
            previousMembership: event.getPrevContent().membership,
            previousEventId: event.getUnsigned().replaces_state,
        });
        const resolve = async () => {
            try {
                const timestamp = await resolveAiBotJoinTimestamp(revision(botMembershipEvent), async eventId => {
                    if (cancelled) return undefined;
                    const loaded = currentRoom.getLiveTimeline().getEvents().find(event => event.getId() === eventId);
                    const event = loaded ?? new MatrixEvent(await client.fetchRoomEvent(currentRoom.roomId, eventId));
                    if (event.getType() !== 'm.room.member' || event.getStateKey() !== aiBotUserId || event.isRedacted()) return undefined;
                    return revision(event);
                });
                if (!cancelled) setResolvedBotJoin({eventId: botMembershipEventId, timestamp});
            } catch {
                if (!cancelled) setResolvedBotJoin({eventId: botMembershipEventId, timestamp: null});
            }
        };
        void resolve();
        return () => { cancelled = true; };
    }, [currentRoom, aiBotUserId, isAiBotInRoom, botMembershipEvent, botMembershipEventId]);

    const aiBotPowerLevels = currentRoom?.currentState
        .getStateEvents('m.room.power_levels', '')
        ?.getContent() as MatrixRoomPowerLevels | undefined;
    const aiBotRoomStatus = currentRoom && aiBotUserId
        ? getAiBotRoomStatus({
            roomMembersLoaded: currentRoom.membersLoaded(),
            botMembership: aiBotMember?.membership,
            powerLevels: aiBotPowerLevels,
            botUserId: aiBotUserId,
        })
        : 'checking';
    const isAiBotPolicyReady = aiBotRoomStatus === 'active';
    const isAiBotRepairRequired = aiBotRoomStatus === 'repairRequired';
    const isAiBotChecking = aiBotRoomStatus === 'checking';
    const isAiBotPending = aiBotRoomStatus === 'pending';
    useEffect(() => {
        setBotActionError(null);
    }, [selectedRoomId, aiBotMember?.membership]);

    const refreshBotStatus = useCallback(async () => {
        if (!selectedRoomId || isRefreshingBot) return;
        setIsRefreshingBot(true);
        setBotActionError(null);
        try {
            await matrixService.refreshRoomState(selectedRoomId);
        } catch {
            setBotActionError({roomId: selectedRoomId, message: 'Could not refresh PhoneHQ AI status. Please retry.'});
        } finally {
            setIsRefreshingBot(false);
        }
    }, [selectedRoomId, isRefreshingBot]);

    const repairAiBotPolicy = useCallback(async () => {
        if (!selectedRoomId || !isAiBotRepairRequired || isInvitingBot || isRepairingBot) return;
        setIsRepairingBot(true);
        try {
            await matrixService.repairAiBotRoomPolicy(selectedRoomId);
        } finally {
            setIsRepairingBot(false);
        }
    }, [selectedRoomId, isAiBotRepairRequired, isInvitingBot, isRepairingBot]);

    const handleRepairAiBotPolicy = useCallback(async () => {
        setBotActionError(null);
        try {
            await repairAiBotPolicy();
            dispatch(showToast({
                title: 'PhoneHQ AI repaired',
                description: 'PhoneHQ AI permissions have been repaired for this room.',
                type: 'success',
            }));
        } catch (error) {
            console.error('Failed to repair PhoneHQ AI room policy:', error);
            if (selectedRoomId) setBotActionError({roomId: selectedRoomId, message: getAiBotRoomErrorDescription(error, 'PhoneHQ AI repair failed. Click the bot to retry.')});
            dispatch(showToast({
                title: 'Failed to repair AI bot',
                description: getAiBotRoomErrorDescription(
                    error,
                    'Could not repair PhoneHQ AI permissions. Please try again.',
                ),
                type: 'error',
            }));
        }
    }, [repairAiBotPolicy, dispatch, selectedRoomId]);

    const handleKickAiBot = useCallback(async () => {
        if (!selectedRoomId || !aiBotUserId || (!isAiBotInRoom && !isAiBotPending) || isKickingBot) return;
        setIsKickingBot(true);
        setBotActionError(null);
        setIsKickBotConfirmOpen(false);
        try {
            await kickUserFromRoom(selectedRoomId, aiBotUserId);
            dispatch(showToast({
                title: 'PhoneHQ AI removed',
                description: 'PhoneHQ AI has been removed from the room.',
                type: 'success'
            }));
        } catch (err) {
            console.error('Failed to kick PhoneHQ AI:', err);
            const isForbidden = isRoomKickForbidden(err);
            dispatch(showToast({
                title: isForbidden ? 'Cannot remove AI bot' : 'Failed to remove AI bot',
                description: isForbidden
                    ? 'You do not have permission to remove users from this room.'
                    : 'Could not remove PhoneHQ AI from the room. Please try again.',
                type: 'error'
            }));
        } finally {
            setIsKickingBot(false);
        }
    }, [selectedRoomId, aiBotUserId, isAiBotInRoom, isAiBotPending, isKickingBot, kickUserFromRoom, dispatch]);

    const handleKickMember = useCallback(async (userId: string) => {
        if (!selectedRoomId) return;

        try {
            await kickUserFromRoom(selectedRoomId, userId);
            dispatch(showToast({
                title: 'Member removed',
                description: 'The member has been removed from the room.',
                type: 'success'
            }));
        } catch (error) {
            console.error('Failed to kick room member:', error);
            const isForbidden = isRoomKickForbidden(error);
            dispatch(showToast({
                title: isForbidden ? 'Cannot remove member' : 'Failed to remove member',
                description: isForbidden
                    ? getRoomKickForbiddenDescription(error)
                    : 'Could not remove the member from the room. Please try again.',
                type: 'error'
            }));
        }
    }, [selectedRoomId, kickUserFromRoom, dispatch]);

    const handleInviteAiBot = useCallback(async () => {
        if (!selectedRoomId || !aiBotUserId || isAiBotInRoom || isInvitingBot) return;
        setIsInvitingBot(true);
        setBotActionError(null);
        try {
            await matrixService.inviteAiBotToRoom(selectedRoomId);
            dispatch(showToast({
                title: 'PhoneHQ AI invited',
                description: 'PhoneHQ AI has been invited. Waiting for the bot to join the room.',
                type: 'success'
            }));
        } catch (err) {
            console.error('Failed to invite PhoneHQ AI:', err);
            setBotActionError({roomId: selectedRoomId, message: getAiBotRoomErrorDescription(err, 'PhoneHQ AI invitation failed. Click the bot to retry.')});
            dispatch(showToast({
                title: 'Failed to invite AI bot',
                description: getAiBotRoomErrorDescription(
                    err,
                    'Could not invite aiBot. Check that the bot is available and try again.',
                ),
                type: 'error'
            }));
        } finally {
            setIsInvitingBot(false);
        }
    }, [selectedRoomId, aiBotUserId, isAiBotInRoom, isInvitingBot, dispatch]);

    const isAiBotBusy = isInvitingBot || isRepairingBot || isKickingBot || isRefreshingBot;
    const aiBotButtonTooltip = (() => {
        if (isRefreshingBot) return 'Refreshing PhoneHQ AI status…';
        if (isAiBotPending) return 'PhoneHQ AI invited — click to refresh status';
        if (aiBotRoomStatus === 'failed') return 'PhoneHQ AI is banned from this room';
        if (isKickingBot) return 'Removing AI bot…';
        if (isRepairingBot) return 'Repairing PhoneHQ AI permissions…';
        if (isInvitingBot) return 'Adding AI bot…';
        if (isAiBotChecking) return 'Checking PhoneHQ AI status…';
        if (isAiBotRepairRequired) return 'PhoneHQ AI permissions need repair — click to retry';
        if (isAiBotInRoom) return 'AI features active — click to remove';
        return 'Click to enable AI features';
    })();
    const aiBotIconColor = isAiBotPolicyReady
        ? 'text-green-500'
        : isAiBotRepairRequired
            ? 'text-amber-500'
            : 'text-ink-muted';

    const scrollToBottomSmooth = useCallback(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, []);

    const handleOpenImageGallery = useCallback((images: ImageInfo[], startIndex: number) => {
        setGalleryImages(images);
        setGalleryStartIndex(startIndex);
        setShowImageGallery(true);
    }, []);


    useEffect(() => {
        if (roomUsers.length !== 1) {
            setUserProfile(null);
            return;
        }

        const matrixId = roomUsers[0]?.userId;
        if (!matrixId) {
            setUserProfile(null);
            return;
        }

        let cancelled = false;

        const resolveAndFetchProfile = async () => {
            try {
                // An empty value is treated as unresolved. This covers the case
                // where the Matrix user existed before the application account.
                let phqUserId = matrixIdToUserIdMap[matrixId] || '';
                if (!phqUserId) {
                    const resolved = await resolveRoomMatrixIds({matrixIds: [matrixId]}).unwrap();
                    phqUserId = resolved[matrixId] || '';
                }

                if (cancelled) return;

                if (!phqUserId) {
                    dispatch(showToast({
                        title: 'Unable to load user profile',
                        description: 'The user account is not available yet. Please try again later.',
                        type: 'error',
                    }));
                    return;
                }

                const user = await fetchUser(phqUserId).unwrap();
                if (!cancelled) setUserProfile(user);
            } catch (error) {
                if (cancelled) return;
                console.error('Failed to resolve user profile:', error);
                dispatch(showToast({
                    title: 'Unable to load user profile',
                    description: 'Please try again later.',
                    type: 'error',
                }));
            }
        };

        setUserProfile(null);
        void resolveAndFetchProfile();

        return () => {
            cancelled = true;
        };
    }, [roomUsers, fetchUser, resolveRoomMatrixIds, dispatch]);

    useEffect(() => {
        if (profileSettingsData?.userTimeZone && userProfile?.userTimeZone) {
            const now = new Date();
            const profileSettingsTime = new Intl.DateTimeFormat('en-US', {
                timeZone: profileSettingsData.userTimeZone,
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
            }).format(now);

            const userProfileTime = new Intl.DateTimeFormat('en-US', {
                timeZone: userProfile.userTimeZone,
                hour12: false,
                hour: '2-digit',
                minute: '2-digit'
            }).format(now);

            setDiffrentTimeZone(profileSettingsTime !== userProfileTime);
        } else {
            setDiffrentTimeZone(false);
        }
    }, [userProfile, profileSettingsData]);

    const usersWithStatuses = useMemo(() => {
        const statusMap = new Map(statuses.map((s: UserStatus) => [String(s.userId), s]));
        return roomUsers?.map((user: RoomUser) => ({
            ...(statusMap.get(getUserId(String(user.userId))) || {} as UserStatus),
            ...user,
        }));
    }, [roomUsers, statuses, getUserId]);

    const infoPanelUsersWithStatuses = useMemo(() => {
        if (userWithoutConversation?.user) {
            const phqUserId = getUserId(userWithoutConversation.matrixId);
            const userStatus = statuses.find((s: UserStatus) => String(s.userId) === phqUserId);

            if (userStatus) {
                return [{
                    ...userStatus,
                    displayName: `${userWithoutConversation.user.firstName} ${userWithoutConversation.user.lastName}`,
                    avatarUrl: userWithoutConversation.user.avatarUrl
                }];
            }
        }

        if (prepareNewChat && usersToCreateChat.length === 1 && !isCreatingGroup) {
            const user: any = usersToCreateChat[0];

            const userStatus: UserStatus = user.customUserStatus ? {
                userId: String(user.userId || user.id),
                firstName: user.firstName,
                lastName: user.lastName,
                position: user.position || '',
                customUserStatus: user.customUserStatus,
                telcoUserStatus: user.telcoUserStatus,
                doNotDisturbUserStatus: user.doNotDisturbUserStatus,
                meetingUserStatus: user.meetingUserStatus
            } : (statuses.find((el: UserStatus) => String(el.userId) === String(user.userId || user.id)) || {} as UserStatus);

            return [{
                ...userStatus,
                displayName: `${user.firstName} ${user.lastName}`,
                avatarUrl: user.avatarUrl,
                userId: user.userId || user.id
            }];
        }

        return usersWithStatuses;
    }, [userWithoutConversation, statuses, getUserId, usersWithStatuses, prepareNewChat, usersToCreateChat, isCreatingGroup]);

    const headerUserStatus = useMemo(() => {
        if (!statuses?.length) return null;

        let userId: string | null = null;

        if (roomUsers?.length) {
            const headerMatrixId = roomUsers[0]?.userId;
            userId = headerMatrixId ? getUserId(headerMatrixId) : null;
        } else if (userWithoutConversation?.matrixId) {
            userId = getUserId(userWithoutConversation.matrixId);
        } else if (prepareNewChat && usersToCreateChat.length === 1 && !isCreatingGroup) {
            userId = String(usersToCreateChat[0].userId);
        }

        if (!userId) return null;
        return statuses.find((status: UserStatus) => String(status.userId) === userId);
    }, [roomUsers, statuses, userWithoutConversation, getUserId, prepareNewChat, usersToCreateChat, isCreatingGroup]);

    useEffect(() => {
        if (headerUserStatus) {
            checkStatusExpiration(headerUserStatus, setIsHeaderStatusExpired);
            checkMeetingStatusExpiration(headerUserStatus, setIsHeaderMeetingExpired);
            const interval = setInterval(() => {
                checkStatusExpiration(headerUserStatus, setIsHeaderStatusExpired);
                checkMeetingStatusExpiration(headerUserStatus, setIsHeaderMeetingExpired);
            }, 5 * 1000);
            return () => clearInterval(interval);
        }
    }, [headerUserStatus?.customUserStatus?.dueDate, headerUserStatus?.meetingUserStatus?.dateTo]);

    const getGMTOffset = (timezone: string) => {
        try {
            const now = new Date();
            const formatter = new Intl.DateTimeFormat('en', {
                timeZone: timezone,
                timeZoneName: 'longOffset'
            });

            const offsetString = formatter.formatToParts(now)
                .find(part => part.type === 'timeZoneName')?.value;

            if (offsetString) {

                return offsetString.replace(':00', '').replace(/GMT\+0(\d)/, 'GMT+$1').replace(/GMT-0(\d)/, 'GMT-$1');
            }

            const targetDate = new Date(now.toLocaleString("en-US", {timeZone: timezone}));
            const utcDate = new Date(now.toLocaleString("en-US", {timeZone: "UTC"}));
            const diff = (targetDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);

            if (diff === 0) return 'GMT';
            const sign = diff > 0 ? '+' : '';
            return `GMT${sign}${Math.round(diff)}`;
        } catch (error) {
            return 'GMT';
        }
    };
    const scrollToEvent = useCallback(async (eventId: string) => {
        if (!eventId) return;
        try {
            setHighlightedMessageId(eventId);

            let messageElement = document.getElementById(`message-${eventId}`);

            if (messageElement) {
                messageElement.scrollIntoView({behavior: 'smooth', block: 'center'});
                messageElement.classList.add('highlight-message');
                return;
            }

            // Message not in DOM — load older messages until it appears
            const MAX_ATTEMPTS = 20;
            for (let i = 0; i < MAX_ATTEMPTS; i++) {
                if (!hasMore) break;
                await loadMoreMessages(100);
                await new Promise(resolve => setTimeout(resolve, 200));

                messageElement = document.getElementById(`message-${eventId}`);
                if (messageElement) {
                    messageElement.scrollIntoView({behavior: 'smooth', block: 'center'});
                    messageElement.classList.add('highlight-message');
                    return;
                }
            }

            console.warn('Message not found after loading older messages:', eventId);
        } catch (error) {
            setHighlightedMessageId(null);
        }
    }, [selectedRoomId, hasMore, loadMoreMessages]);

    const pendingChatMessage = (location.state as Partial<OpenChatMessageLocationState> | null)?.openChatMessage;
    const [chatMessageToFocus, setChatMessageToFocus] = useState<OpenChatMessageLocationState['openChatMessage'] | null>(null);
    useEffect(() => {
        if (!pendingChatMessage || !isInitialized || !isAuthenticated) return;
        navigate(location.pathname, {replace: true, state: null});
        setChatMessageToFocus(pendingChatMessage);
        void selectRoom(pendingChatMessage.roomId);
    }, [pendingChatMessage, isInitialized, isAuthenticated, navigate, location.pathname, selectRoom]);
    useEffect(() => {
        if (!chatMessageToFocus || selectedRoomId !== chatMessageToFocus.roomId || !hasScrolledForRoomRef.current) return;
        setChatMessageToFocus(null);
        if (chatMessageToFocus.eventId) void scrollToEvent(chatMessageToFocus.eventId);
    }, [chatMessageToFocus, selectedRoomId, groupedMessages, isLoadingMessages, scrollToEvent]);
    const startPreparingChat = async (usersWithMatrixIds: UserWithMatrixId[], optionalGroupName?: string, forceGroupMode?: boolean) => {
        const matrixIds = usersWithMatrixIds.map(u => u.matrixId);
        const normalizedGroupName = optionalGroupName?.trim();

        const isGroupCreation = normalizedGroupName !== undefined || forceGroupMode === true;

        if (!isGroupCreation) {
            let existingRoomId = matrixService.findDirectMessageRoom(matrixIds);

            if (!existingRoomId && usersWithMatrixIds.length === 1) {
                existingRoomId = findDirectRoomByUserId({
                    rooms: roomsWithLastMessages,
                    targetUserId: String(usersWithMatrixIds[0].id),
                    matrixIdToUserId: matrixIdToUserIdMap,
                    isDirectRoom: room => matrixService.isDirectMessageRoom(room),
                });
            }

            if (existingRoomId) {
                await selectRoom(existingRoomId);
                return existingRoomId;
            }
        }

        if (isGroupCreation && normalizedGroupName) {
            const existingNamedRoomId = matrixService.findNamedRoom(normalizedGroupName, matrixIds, true);
            if (existingNamedRoomId) {
                // Same members under the same name: ask rather than silently
                // reopening it, and say that a different name is what makes a
                // second group possible.
                setDuplicateGroupRoomId(existingNamedRoomId);
                setDuplicateGroupIsNamed(true);
                setShowCreateRoom(false);
                return existingNamedRoomId;
            }
        }

        if (isGroupCreation && !normalizedGroupName) {
            const existingUnnamedRoomId = matrixService.findUnnamedGroupRoom(matrixIds, true);
            if (existingUnnamedRoomId) {
                // Ask rather than silently reopening it — see DuplicateGroupDialog.
                setDuplicateGroupRoomId(existingUnnamedRoomId);
                setDuplicateGroupIsNamed(false);
                setShowCreateRoom(false);
                return existingUnnamedRoomId;
            }
        }

        pendingRoomCreationRef.current = null;
        setPrepareNewChat(true);
        setIsCreatingGroup(isGroupCreation);
        setShowCreateRoom(false)
        selectRoom(null);
        setUsersToCreateChat(usersWithMatrixIds);
        setOptionalGroupName(normalizedGroupName || '')

        // Remove old pending local room if exists
        if (pendingLocalRoomId) {
            matrixService.removeLocalRoom(pendingLocalRoomId);
        }

        // Create local room immediately so it appears on the list
        const localId = matrixService.createLocalRoom({
            name: isGroupCreation ? (normalizedGroupName || undefined) : undefined,
            otherUsers: usersWithMatrixIds.map(u => ({
                userId: u.matrixId,
                displayName: `${u.firstName} ${u.lastName}`.trim(),
                avatarUrl: u.avatarUrl || ''
            })),
            isDirect: !isGroupCreation && usersWithMatrixIds.length === 1,
        });
        setPendingLocalRoomId(localId);

    }
    const renderChatHeader = () => {
        const headerUsers = roomUsers.length ? roomUsers :
            userWithoutConversation ? [{
                    userId: userWithoutConversation?.user.userId,
                    displayName: userWithoutConversation?.user.firstName + ' ' + userWithoutConversation?.user.lastName,
                    avatarUrl: userWithoutConversation?.user.avatarUrl,
                }] :
                (prepareNewChat && usersToCreateChat.length) ? usersToCreateChat.map(user => ({
                    userId: user.userId,
                    displayName: getFullName(user.firstName, user.lastName),
                    avatarUrl: user.avatarUrl,
                })) : null;

        const {
            dateString: headerMeetingDateString,
            timeString: headerMeetingTimeString
        } = convertFromISOFormat(headerUserStatus?.meetingUserStatus?.dateTo || '');
        const headerMeetingExpires = headerMeetingDateString ? `${headerMeetingDateString}, ${headerMeetingTimeString}` : headerMeetingTimeString;

        const isDMChat = currentRoom ? isCurrentRoomDirect : headerUsers?.length === 1;
        const showGroupAvatar = currentRoom ? !isCurrentRoomDirect : (headerUsers?.length || 0) > 1;
        const headerAvatarUsers = currentRoom ? roomAvatarUsers : headerUsers;
        const headerAvatarNames = headerAvatarUsers?.map(user => user.displayName).filter(Boolean) || [];
        const headerAvatarTooltip = headerAvatarNames.length > 2
            ? `${headerAvatarNames.slice(0, 2).join(', ')} +${headerAvatarNames.length - 2}`
            : headerAvatarNames.join(', ') || 'Conversation details';

        const status = getUserStatus(
            statuses?.find(s => String(s.userId) === getUserId(String(headerUsers?.[0]?.userId)))
        ) as TelcoStatus | undefined;
        const callButtonInput = {
            selfBusy: callState !== CallStateEnum.IDLE,
            peerTalking: status == TelcoStatus.TALKING,
            peerOffline: status == TelcoStatus.OFFLINE,
        };
        const audioCallButton = directCallButton(callButtonInput);

        return (
            <div className="flex min-h-header items-center border-0 border-b border-solid border-line bg-surface px-4 py-2">
                <div className={'flex w-full items-center gap-1 text-footnote'}>
                    {(prepareNewChat && (isCreatingGroup || usersToCreateChat.length > 1)) &&
                        <UserListToCreateRoom
                            prepareNewChat={prepareNewChat}
                            usersToCreateChat={usersToCreateChat}
                            setUsersToCreateChat={setUsersToCreateChat}
                            setPrepareNewChat={setPrepareNewChat}
                            setIsCreatingGroup={setIsCreatingGroup}
                            getFullName={getFullName}
                            onCancel={() => {
                                if (pendingLocalRoomId) {
                                    matrixService.removeLocalRoom(pendingLocalRoomId);
                                    setPendingLocalRoomId(null);
                                }
                            }}
                        />
                    }
                    {!isCreatingGroup && (
                        <button
                            type="button"
                            className="flex items-center flex-1 min-w-0 text-left"
                            onClick={() => openInfoPanel()}
                            data-tooltip-bottom={headerAvatarTooltip}
                            aria-label={isDMChat ? 'View profile' : 'View group info'}
                        >
                            {(showGroupAvatar && headerAvatarUsers) ?
                                // Match the conversation list, where a group with
                                // its own avatar gets 32px and a stacked pair gets
                                // 20px — a fixed 20px here left the header icon
                                // smaller than the same group's icon in the list.
                                (currentRoom?.getMxcAvatarUrl()
                                    ? <RoomAvatar room={currentRoom} roomUsers={headerAvatarUsers?.slice(0, 2) || []}
                                                  size="32px" className={'mr-3'} />
                                    : <GroupAvatarTile className="mr-3" />) :
                                (headerUsers && <PhoneHQAvatar
                                                               name={headerUsers?.[0]?.displayName || ''}
                                                               size={'32px'}
                                                               className={'mr-3'}
                                                               src={headerUsers?.[0]?.avatarUrl || undefined}
                                                               status={getUserStatus(statuses?.find(s => String(s.userId) === getUserId(String(headerUsers?.[0]?.userId))))}
                                />)}
                            <div className="flex min-w-0 flex-col">
                                {!showCreateRoom && <h3 className="m-0 truncate text-body-strong text-ink">
                                    {!currentRoom?.name.includes('chat.chte.ch') ? currentRoom?.name : headerUsers?.map(el => el.displayName).join(' ')}
                                </h3>}

                                {isDMChat && headerUserStatus && !showCreateRoom && (
                                    <div className="flex flex-col gap-0.5">
                                        {(!isHeaderStatusExpired && headerUserStatus?.customUserStatus?.icon) && (
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center">
                                                    <div className="mr-2 flex items-center justify-center">
                                                        {hexToEmoji(headerUserStatus?.customUserStatus?.icon, '!w-3 !h-3')}
                                                    </div>
                                                    <span className="w-[300px] truncate text-footnote text-ink-muted">{headerUserStatus?.customUserStatus?.name || 'No status description'}</span>
                                                </div>
                                            </div>
                                        )}
                                        {(!isHeaderMeetingExpired && headerUserStatus?.meetingUserStatus?.status === 'IN_MEETING') && (
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 mr-2">
                                                        <img className="w-full h-full" src={googleCalendarIcon} alt="Meeting"/>
                                                    </div>
                                                    <div className="flex items-center gap-1 text-xs">
                                                        <span className="text-footnote text-ink-muted">In a meeting</span>
                                                        {headerMeetingExpires && (
                                                            <span className="text-footnote text-ink-muted">until {headerMeetingExpires}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </button>
                    )}
                    {currentRoom && !isCurrentRoomDirect && showsCallBadge(roomCallEntry) && <CallInProgressBadge roomId={currentRoom.roomId}/>}
                    <GroupRoomCallButtons entry={roomCallEntry} roomId={currentRoom?.roomId}/>
                    {roomCallEntry.kind === 'one_to_one' && userProfile && (
                        <button
                            type="button"
                            data-testid="direct-call-start"
                            data-tooltip-left={audioCallButton.tooltip}
                            aria-label={audioCallButton.ariaLabel}
                            aria-disabled={audioCallButton.ariaDisabled || undefined}
                            className={`relative ${audioCallButton.className}`}
                            onClick={() => {
                                if (blockCallDuringGroupCall(callState, dispatch)) return;
                                if (!selectedRoomId || callState !== CallStateEnum.IDLE) return;
                                dispatch(openDirectPrep({
                                    roomId: selectedRoomId,
                                    intent: 'audio',
                                    peer: {
                                        userId: userProfile?.userId || '',
                                        firstName: userProfile?.firstName || '',
                                        lastName: userProfile?.lastName || '',
                                        avatarUrl: userProfile?.avatarUrl || '',
                                    },
                                }));
                            }}
                        >
                            <Phone size={32} className="button p-1.5"/>
                            <span
                                className={headerUserStatusStyles[status || TelcoStatus.UNKNOWN]}
                            />
                        </button>
                    )}

                    {/* AI indicator button */}
                    {isAiBotEnabled && selectedRoomId && !showCreateRoom && (
                        <>
                            <button
                                onClick={() => {
                                    if (isAiBotChecking || isAiBotPending) {
                                        void refreshBotStatus();
                                    } else if (isAiBotRepairRequired) {
                                        void handleRepairAiBotPolicy();
                                    } else if (isAiBotInRoom) {
                                        setIsKickBotConfirmOpen(true);
                                    } else {
                                        void handleInviteAiBot();
                                    }
                                }}
                                disabled={isAiBotBusy || aiBotRoomStatus === 'failed'}
                                data-tooltip-bottom={aiBotButtonTooltip}
                                type="button"
                                className="relative"
                            >
                                <Bot
                                    className={`button ${aiBotIconColor} p-1.5`}
                                    size={32}
                                />
                                {isAiBotBusy && (
                                    <span className={headerBotStatusStyles.loading}/>
                                )}
                                {!isAiBotBusy && isAiBotInRoom && isAiBotPolicyReady && (
                                    <span className={headerBotStatusStyles.active}/>
                                )}
                                {!isAiBotBusy && isAiBotRepairRequired && (
                                    <span className={headerBotStatusStyles.repairRequired}/>
                                )}
                                {!isAiBotBusy && isAiBotChecking && (
                                    <span className={headerBotStatusStyles.checking}/>
                                )}
                                {!isAiBotBusy && aiBotRoomStatus === 'inactive' && (
                                    <span className={headerBotStatusStyles.inactive}/>
                                )}
                            </button>
                            {isAiBotPending && selectedRoomId && <AiBotPendingNotice
                                roomId={selectedRoomId}
                                busy={isAiBotBusy}
                                onRefresh={refreshBotStatus}
                                onCancel={() => setIsKickBotConfirmOpen(true)}
                            />}
                            {botActionError?.roomId === selectedRoomId && <span role="alert">{botActionError.message}</span>}
                            <PhoneHQDialog
                                open={isKickBotConfirmOpen}
                                onOpenChange={setIsKickBotConfirmOpen}
                            >
                                <div className="p-2">
                                    <h3 className="text-base font-semibold text-ink mb-2">Remove AI bot from room?</h3>
                                    <p className="text-sm text-ink-muted-strong mb-4">
                                        PhoneHQ AI will be removed from this room and AI features will no longer be available. You can invite it back at any time.
                                    </p>
                                    <PhoneHQDialogFooter>
                                        <PhoneHQButton variant="tertiary" onClick={() => setIsKickBotConfirmOpen(false)}>
                                            Cancel
                                        </PhoneHQButton>
                                        <PhoneHQButton onClick={handleKickAiBot}>
                                            Remove
                                        </PhoneHQButton>
                                    </PhoneHQDialogFooter>
                                </div>
                            </PhoneHQDialog>
                        </>
                    )}

                    {/* Search button */}
                    {selectedRoomId && !showCreateRoom && (
                        <>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        setShowSearchPanel(!showSearchPanel);
                                        setShowInfoPanel(false);
                                        setShowKnowledgeBasePanel(false);
                                        if (showSearchPanel) setMessageSearchQuery('');
                                    }}

                                    data-tooltip-bottom="Search in chat"
                                    type="button"
                                >
                                    <Search className="button p-1.5" size={32}/>
                                </button>

                            </div>
                        </>
                    )}

                    {/* Info button */}
                    {selectedRoomId && !showCreateRoom && (
                        <button
                            onClick={() => {
                                setProfileUserIdToView(null);
                                setShowSearchPanel(false);
                                setShowKnowledgeBasePanel(false);
                                setShowInfoPanel(show => !show);
                            }}
                            data-tooltip-left="Info"
                        >
                            <Info className="button p-1.5" size={32}/>

                        </button>
                    )}

                    {/* Knowledge Base button */}
                    {selectedRoomId && !showCreateRoom && isAiKnowledgeBaseEnabled && (
                        <button
                            onClick={() => {
                                setShowSearchPanel(false);
                                setShowInfoPanel(false);
                                setShowKnowledgeBasePanel(!showKnowledgeBasePanel);
                            }}
                            data-tooltip-left="Ask Knowledge Base"
                        >
                            <BookOpen className="button p-1.5" size={32}/>
                        </button>
                    )}
                </div>
            </div>
        )
    };


    const renderGroupedMessages = () => {
        if (isLoadingMessages) {
            return (
                <div className="text-center text-ink-muted py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Loading messages...</p>
                </div>
            );
        }

        if (groupedMessages.length === 0) {
            return;
        }

        return (
            <>
                {[...groupedMessages].reverse().map((group) => (
                    <MessageGroupComponent
                        key={group.date}
                        group={group}
                        selectedRoomId={selectedRoomId!}
                        onReply={handleReply}
                        onEdit={handleEdit}
                        onPrivateReply={handlePrivateReply}
                        sendReaction={sendReaction}
                        removeReaction={removeReaction}
                        getClient={getClient}
                        getCurrentUserId={getCurrentUserId}
                        highlightedMessageId={highlightedMessageId}
                        scrollToEvent={scrollToEvent}
                        isGroupChat={roomUsers.length > 1}
                        isAiBotInRoom={isAiBotInRoom}
                        isRoomEncrypted={isRoomEncrypted(selectedRoomId!)}
                        aiBotJoinedAt={aiBotJoinedAt}
                        searchQuery={messageSearchQuery}
                        onOpenImageGallery={handleOpenImageGallery}
                        onMediaLoad={handleMediaLoad}
                        onViewProfile={viewChatUser}
                        onOpenRoomMention={openRoomMention}
                        onUnresolvedMention={reportUnresolvedMention}
                        callEntries={callEntries}
                        onCallBack={canCallBack ? callBackPeer : undefined}
                    />
                ))}
                <div className={'flex items-center justify-center mt-4 mb-2'}>
                    {selectedRoomId && <EncryptedStatus isEncrypted={isRoomEncrypted(selectedRoomId)}/>}
                </div>
            </>
        );
    };

    if (!isInitialized) {
        const isStandby = sessionLockState === 'standby';
        const isTakeoverPending = sessionLockState === 'takeover_pending';
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    {!isStandby && (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    )}
                    <p className="text-ink-muted-strong">
                        {isTakeoverPending
                            ? 'Moving chat to this window...'
                            : isStandby
                                ? 'Chat is active in another window.'
                            : 'Initializing chat...'}
                    </p>
                    {isStandby && (
                        <>
                            <p className="mt-2 text-sm text-ink-muted">
                                Continue here to stop chat in the other window and reconnect in this one.
                            </p>
                            <PhoneHQButton
                                type="button"
                                onClick={requestSessionTakeover}
                                className="mt-4"
                            >
                                Continue here
                            </PhoneHQButton>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full bg-foreground relative">
            {/* Sidebar with RoomList Component */}
            <RoomList
                roomsWithLastMessages={roomsWithLastMessages}
                selectedRoomId={selectedRoomId}
                onSelectRoom={handleSelectRoom}
                onNewConversationClick={(selectedUser: UserDirectoryItem) => {
                    let existingRoomId = matrixService.findDirectMessageRoom([selectedUser.user_id]);
                    if (!existingRoomId) {
                        const targetUserId = getUserId(selectedUser.user_id);
                        if (targetUserId) {
                            existingRoomId = findDirectRoomByUserId({
                                rooms: roomsWithLastMessages,
                                targetUserId: String(targetUserId),
                                matrixIdToUserId: matrixIdToUserIdMap,
                                isDirectRoom: room => matrixService.isDirectMessageRoom(room),
                            });
                        }
                    }
                    if (existingRoomId) {
                        void handleSelectRoom(existingRoomId);
                        return;
                    }
                    const phqUserId = getUserId(selectedUser.user_id);
                    if (phqUserId) {
                        fetchUser(phqUserId).unwrap().then(user => {
                            handleSelectRoom('').then(() =>
                                setUserWithoutConversation({
                                    user,
                                    matrixId: selectedUser.user_id
                                })
                            )
                        });
                    }
                }}
                onCreateRoom={() => setShowCreateRoom(true)}
                client={getClient()}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onLoadMoreRooms={loadMoreRooms}
                hasMoreRooms={hasMoreRooms}
            />


            {/* Main Chat Area */}
            <div className={`relative flex-1 flex flex-col transition-all duration-300 mx-auto ${
                (showInfoPanel || showSearchPanel || showKnowledgeBasePanel) ? 'mr-80' : 'mr-0'
            }`}>


                {(selectedRoomId || userWithoutConversation || usersToCreateChat.length) ? (
                    <>
                        {/* Chat Header */}
                        {renderChatHeader()}
                        {selectedRoomId && !isCurrentRoomDirect && (
                            <ChatCallBanner roomId={selectedRoomId} entry={roomCallEntry} onHeight={setCallBannerHeight}/>
                        )}
                        {/* Messages */}
                        <div className='px-6'>
                            {!isMessagesReady && (!usersToCreateChat.length && !userWithoutConversation) && (
                                <div
                                    className="flex items-center justify-center bg-surface w-full max-w-[848px] mx-auto animate-fadeIn"
                                    style={{height: `calc(100vh - ${inputHeight}px - 100px - ${callBannerHeight}px)`}}>
                                    <PhoneHQSpinner/>
                                </div>
                            )}
                            <div
                                ref={setMessagesContainerRef}
                                className={`messages-container ${(userWithoutConversation || usersToCreateChat.length) ? '!flex-col' : ''} overflow-y-auto pr-4 pb-10 w-full max-w-[848px] mx-auto bg-surface transition-opacity duration-300`}
                                style={{
                                    height: `calc(100vh - ${inputHeight}px - 100px - ${callBannerHeight}px)`,
                                    display: (isMessagesReady || usersToCreateChat.length || userWithoutConversation) ? 'flex' : 'none',
                                    opacity: (isMessagesReady || usersToCreateChat.length || userWithoutConversation) ? 1 : 0
                                }}
                            >
                                {userWithoutConversation && <ConversationStart
                                    status={statuses.find((s: UserStatus) => String(s.userId) === getUserId(userWithoutConversation.matrixId))}
                                    usersData={userWithoutConversation.user}
                                    setShowInfoPanel={setShowInfoPanel}
                                />}
                                {(prepareNewChat && usersToCreateChat.length === 1 && !isCreatingGroup) && (() => {
                                    const user: any = usersToCreateChat[0];
                                    // Extract status from user object (comes from CreateRoomModal's chatUsers)
                                    const userStatus: UserStatus | undefined = user.customUserStatus ? {
                                        userId: String(user.userId || user.id),
                                        firstName: user.firstName,
                                        lastName: user.lastName,
                                        position: user.position || '',
                                        customUserStatus: user.customUserStatus,
                                        telcoUserStatus: user.telcoUserStatus,
                                        doNotDisturbUserStatus: user.doNotDisturbUserStatus,
                                        meetingUserStatus: user.meetingUserStatus
                                    } : statuses.find((el: UserStatus) => String(el.userId) === String(user.userId || user.id));

                                    return (
                                        <ConversationStart
                                            status={userStatus}
                                            usersData={user}
                                            setShowInfoPanel={setShowInfoPanel}
                                        />
                                    );
                                })()}
                                {
                                    (prepareNewChat && (isCreatingGroup || usersToCreateChat.length > 1)) &&
                                    <div>
                                        <div className={'flex-col justify-center mt-10 '}>
                                            <div className={'flex justify-center gap-5 h-16'}>
                                                <StackedAvatars
                                                    size={'40px'}
                                                    users={usersToCreateChat.map(user => ({
                                                        displayName: getFullName(user.firstName, user.lastName) || '',
                                                        avatarUrl: user.avatarUrl || ''
                                                    }))}
                                                />
                                            </div>
                                            <div
                                                className={'font-semibold text-base text-ink mt-5 text-center flex-wrap max-w-[400px] mx-auto'}>
                                                {optionalGroupName || usersToCreateChat.map(user => getFullName(user.firstName, user.lastName)).join(' ')}
                                                <div>
                                                    <PhoneHQDialog
                                                        open={isGroupNameDialogOpen}
                                                        onOpenChange={(open) => {
                                                            setIsGroupNameDialogOpen(open);
                                                            if (open) {
                                                                setGroupName(optionalGroupName || '');
                                                            }
                                                        }}
                                                        trigger={<button
                                                            className="text-sm font-medium text-brand-ink hover:text-brand-ink transition-colors"
                                                        >
                                                            Change group name
                                                        </button>
                                                        }
                                                        data-tooltip="Change group name" theme="light" close={true}>
                                                        <PhoneHQInput
                                                            label={'Group name'}
                                                            value={groupName}
                                                            onChange={(e) => {
                                                                setGroupName(e.target.value);
                                                            }}
                                                            placeholder="Enter group name..."/>
                                                        <PhoneHQDialogFooter>
                                                            <PhoneHQButton variant='tertiary'
                                                                           className='mt-4 mr-2'
                                                                           onClick={() => {
                                                                               setIsGroupNameDialogOpen(false);
                                                                           }}>Cancel</PhoneHQButton>
                                                            <PhoneHQButton className='mt-4' onClick={() => {
                                                                setOptionalGroupName(groupName);
                                                                setIsGroupNameDialogOpen(false);
                                                            }}>Save</PhoneHQButton>
                                                        </PhoneHQDialogFooter>
                                                    </PhoneHQDialog>
                                                </div>
                                                <div className={'flex items-center justify-center mt-4 mb-2'}>
                                                    <EncryptedStatus isEncrypted={true}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }

                                <div ref={messagesEndRef}/>
                                {renderGroupedMessages()}
                            </div>
                        </div>
                        {typingMembers.length > 0 && (
                            <div className="pointer-events-none relative flex h-0 items-end justify-center">
                                <div className="w-full max-w-[848px]">
                                    <div className="typing-indicator flex items-center gap-2 rounded-t-2xl bg-canvas px-4 pt-2 pb-6 -mb-5 border border-line !text-[11px] text-ink">
                                        <div className="flex shrink-0 -space-x-1">
                                            {typingMembers.slice(0, 3).map(member => (
                                                <PhoneHQAvatar
                                                    key={member.userId}
                                                    name={member.displayName}
                                                    size={'20px'}
                                                    className="border border-white"
                                                    src={member.avatarUrl}
                                                />
                                            ))}
                                        </div>
                                        <span className="min-w-0 truncate">{typingIndicatorText}</span>
                                        <span className="flex shrink-0 items-center gap-0.5" aria-hidden="true">
                                            <span
                                                className="typing-dot h-1 w-1 rounded-full bg-zinc-400"
                                                style={{animationDelay: '0ms'}}
                                            />
                                            <span
                                                className="typing-dot h-1 w-1 rounded-full bg-zinc-400"
                                                style={{animationDelay: '150ms'}}
                                            />
                                            <span
                                                className="typing-dot h-1 w-1 rounded-full bg-zinc-400"
                                                style={{animationDelay: '300ms'}}
                                            />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className={'w-full flex justify-center'}>
                            {showScrollButton && (
                                <div style={
                                    {
                                        bottom: inputHeight + 50 + 'px'
                                    }
                                } onClick={scrollToBottomSmooth}
                                     className={`absolute p-3 z-30 rounded-full border border-fill-subtle  shadow-md bg-surface`}>

                                    <ArrowDown size={20} className={'cursor-pointer !text-brand-ink'}/>
                                </div>
                            )}
                        </div>


                        <ChatMessageInput
                            selectedRoomId={selectedRoomId}
                            maxAttachments={MAX_ATTACHMENTS_PER_MESSAGE}
                            roomUsers={roomUsers}
                            usersToCreateChat={userWithoutConversation ? [{
                                matrixId: userWithoutConversation.matrixId,
                                firstName: userWithoutConversation.user.firstName,
                                lastName: userWithoutConversation.user.lastName,
                                avatarUrl: userWithoutConversation.user.avatarUrl
                            }] : usersToCreateChat.map(u => ({
                                matrixId: u.matrixId,
                                firstName: u.firstName,
                                lastName: u.lastName,
                                avatarUrl: u.avatarUrl
                            }))}
                            availableRooms={roomMentionTargets}
                            currentRoomName={!currentRoom?.name.includes('chat.chte.ch') ? currentRoom?.name : undefined}
                            replyToMessage={replyToMessage}
                            onReplyCancel={() => setReplyToMessage(null)}
                            privateReplyTarget={privateReply ? {senderName: privateReply.senderName, excerpt: privateReply.excerpt} : null}
                            onPrivateReplyCancel={() => setPrivateReply(null)}
                            editingMessage={editingMessage?.eventId ?? null}
                            editingBody={editingMessage?.body ?? null}
                            editingMentions={editingMessage?.mentions ?? EMPTY_MENTIONS}
                            editingRoomMentions={editingMessage?.roomMentions ?? EMPTY_ROOM_MENTIONS}
                            onEditCancel={() => setEditingMessage(null)}
                            isRoomEncrypted={resolveComposerIsRoomEncrypted(selectedRoomId, isRoomEncrypted)}
                            onSendMessage={(message, mentions, files, roomMentions) => {
                                if (files.length > MAX_ATTACHMENTS_PER_MESSAGE) {
                                    dispatch(showToast({
                                        title: 'Too many attachments',
                                        description: `You can send up to ${MAX_ATTACHMENTS_PER_MESSAGE} attachments per message`,
                                        type: 'error'
                                    }));
                                    return Promise.reject(new Error(`Attachment limit exceeded: ${files.length}/${MAX_ATTACHMENTS_PER_MESSAGE}`));
                                }

                                const editing = editingMessage;
                                if (editing) {
                                    const editedText = message?.trim() || '';
                                    if (files.length > 0) {
                                        dispatch(showToast({
                                            title: intl.formatMessage({id: 'chat.edit.attachments-not-allowed.title', defaultMessage: 'Attachments can\'t be added to an edit'}),
                                            description: intl.formatMessage({id: 'chat.edit.attachments-not-allowed.description', defaultMessage: 'Editing only changes the message text'}),
                                            type: 'error'
                                        }));
                                        return Promise.reject(new Error('Attachments are not allowed while editing'));
                                    }
                                    if (!selectedRoomId || !editedText) {
                                        return Promise.reject(new Error('Edited message must not be empty'));
                                    }

                                    const roomIdForEdit = selectedRoomId;
                                    setEditingMessage(null);
                                    void (async () => {
                                        try {
                                            await sendEditMessage(roomIdForEdit, editing.eventId, editedText, mentions, roomMentions);
                                        } catch (error) {
                                            console.error('Failed to edit message:', error);
                                            dispatch(showToast({
                                                title: intl.formatMessage({id: 'chat.edit.save-failed.title', defaultMessage: 'Couldn\'t save the edit'}),
                                                description: intl.formatMessage({id: 'chat.edit.save-failed.description', defaultMessage: 'Try again in a moment'}),
                                                type: 'error'
                                            }));
                                            setEditingMessage({eventId: editing.eventId, body: editedText, mentions, roomMentions: roomMentions ?? []});
                                        }
                                    })();
                                    return Promise.resolve();
                                }

                                const replyTo = replyToMessage;
                                if (replyToMessage) {
                                    setReplyToMessage(null);
                                }
                                const privateReplyTarget = privateReply;
                                if (privateReply) {
                                    setPrivateReply(null);
                                }

                                void (async () => {
                                    try {
                                        if (!selectedRoomId && (userWithoutConversation || usersToCreateChat.length)) {
                                            const matrixIds = userWithoutConversation ? [userWithoutConversation.matrixId] : usersToCreateChat.map(u => u.matrixId);
                                            const roomId = await handleCreateRoom(matrixIds, optionalGroupName);
                                            setPrepareNewChat(false);
                                            setIsCreatingGroup(false);
                                            setUserWithoutConversation(null);
                                            setUsersToCreateChat([]);
                                            setSearchTerm('');

                                            if (roomId) {
                                                const caption = message?.trim() || '';

                                                if (files.length > 0) {
                                                    const batchId = files.length > 1 ? crypto.randomUUID() : undefined;
                                                    for (let i = 0; i < files.length; i++) {
                                                        await sendFile(
                                                            roomId,
                                                            files[i],
                                                            replyTo || undefined,
                                                            i === 0 ? caption : undefined,
                                                            batchId,
                                                            i === 0 ? mentions : undefined
                                                        );
                                                    }
                                                }

                                                if (caption && files.length === 0) {
                                                    if (replyTo) {
                                                        await sendReplyMessage(roomId, caption, replyTo, mentions, roomMentions);
                                                    } else {
                                                        await sendMessage(roomId, caption, mentions, undefined, roomMentions);
                                                    }
                                                }
                                                await markMessagesAsRead(roomId);

                                                requestAnimationFrame(() => {
                                                    requestAnimationFrame(() => {
                                                        if (messagesContainerRef.current) {
                                                            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                                                        }
                                                    });
                                                });
                                            }
                                        } else if (selectedRoomId) {
                                            const caption = message?.trim() || '';

                                            if (files.length > 0) {
                                                const batchId = files.length > 1 ? crypto.randomUUID() : undefined;
                                                for (let i = 0; i < files.length; i++) {
                                                    await sendFile(
                                                        selectedRoomId,
                                                        files[i],
                                                        privateReplyTarget ? undefined : (replyTo || undefined),
                                                        i === 0 ? caption : undefined,
                                                        batchId,
                                                        i === 0 ? mentions : undefined,
                                                        i === 0 ? (privateReplyTarget ?? undefined) : undefined
                                                    );
                                                }
                                            }

                                            if (caption && files.length === 0) {
                                                if (privateReplyTarget) {
                                                    await sendPrivateReplyMessage(selectedRoomId, caption, privateReplyTarget, mentions, roomMentions);
                                                } else if (replyTo) {
                                                    await sendReplyMessage(selectedRoomId, caption, replyTo, mentions, roomMentions);
                                                } else {
                                                    await sendMessage(selectedRoomId, caption, mentions, undefined, roomMentions);
                                                }
                                            }
                                            await markMessagesAsRead(selectedRoomId);

                                            requestAnimationFrame(() => {
                                                requestAnimationFrame(() => {
                                                    if (messagesContainerRef.current) {
                                                        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                                                    }
                                                });
                                            });
                                        }
                                    } catch (error) {
                                        console.error('Failed to send message:', error);
                                    }
                                })();

                                return Promise.resolve();
                            }}
                            onAttachmentLimitExceeded={(maxAttachments) => {
                                dispatch(showToast({
                                    title: 'Too many attachments',
                                    description: `You can send up to ${maxAttachments} attachments per message`,
                                    type: 'error'
                                }));
                            }}
                            onTyping={() => {
                                if (selectedRoomId) {
                                    sendTypingNotification(selectedRoomId);
                                    prepareRoomEncryption(selectedRoomId);
                                }
                            }}
                            getReplyMessagePreview={() => {
                                if (!selectedRoomId) return null;
                                const messages = matrixService.getRoomMessages(selectedRoomId, 100);
                                const replyToMsg = messages.find(msg => msg.getId() === replyToMessage);
                                if (replyToMsg) {
                                    const content = replyToMsg.getContent();
                                    const sender = replyToMsg.getSender();
                                    const room = matrixService.getRoom(selectedRoomId);
                                    const member = room?.getMember(sender || '');
                                    const displayName = member?.rawDisplayName || sender || 'Unknown';

                                    let preview = content.body || '';
                                    if (content.msgtype === 'm.image') preview = '📷 Image';
                                    else if (content.msgtype === 'm.file') preview = `📎 ${content.filename || 'File'}`;
                                    else if (content.msgtype === 'm.audio') preview = '🎵 Audio';
                                    else if (content.msgtype === 'm.video') preview = '🎥 Video';

                                    return (
                                        <>
                                            <span
                                                className={'text-ink-secondary font-medium'}>Replying to {displayName}</span>
                                            <span className="text-ink-muted">
                                                {displayName}: {preview.substring(0, 50)}{preview.length > 50 ? '...' : ''}
                                            </span>
                                        </>
                                    );
                                }
                                return null;
                            }}
                            inputHeight={inputHeight}
                            onHeightChange={setInputHeight}
                            diffrentTimeZone={diffrentTimeZone}
                            onDismissTimezone={() => setDiffrentTimeZone(false)}
                            timezoneMessage={
                                <div
                                    className="px-4 py-2 text-ink-muted relative items-center bg-surface-muted rounded-t-2xl mx-auto !text-[11px] h-14 top-5 max-w-[848px]">
                                    {roomUsers[0]?.displayName || getFullName(userProfile?.firstName, userProfile?.lastName) || 'User'} is in a different time zone
                                    ({getGMTOffset(userProfile?.userTimeZone)}) Local time: {
                                    (() => {
                                        try {
                                            return new Date().toLocaleTimeString('en-US', {
                                                timeZone: userProfile?.userTimeZone,
                                                hour12: true,
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }).toLowerCase();
                                        } catch (error) {
                                            return 'Invalid timezone';
                                        }
                                    })()
                                }
                                    <span onClick={() => setDiffrentTimeZone(false)}
                                          className={'text-ink underline px-2 cursor-pointer font-medium'}>
                                        {'Got it'}
                                    </span>
                                </div>
                            }
                        />
                    </>
                ) : (
                    <div>

                    </div>
                )}
            </div>

            {/* Search Panel - positioned absolutely to slide in from the right */}
            {showSearchPanel && (
                <div className={`absolute right-0 top-0 h-full z-40 transition-all duration-300 ease-in-out w-80 opacity-100`}>
                    <MatrixMessageSearch onResultClick={scrollToEvent}
                                         selectedRoomId={selectedRoomId!}
                                         setShowSearchPanel={setShowSearchPanel}
                                         onSearchQueryChange={setMessageSearchQuery}
                    />
                </div>
            )}

            {/* Info Panel - positioned absolutely to slide in from the right */}
            {((selectedRoomId && currentRoom && roomUsers) || userWithoutConversation || (prepareNewChat && usersToCreateChat.length === 1 && !isCreatingGroup)) && (
                <div className={`absolute right-0 top-0 h-full z-40 transition-all duration-300 ease-in-out ${
                    showInfoPanel
                        ? 'w-80 opacity-100'
                        : '!w-0 p-0 opacity-0 overflow-hidden'
                }`}>
                    <InfoPanel
                        room={currentRoom}
                        isRoomDirect={isCurrentRoomDirect}
                        roomUsers={(prepareNewChat && usersToCreateChat.length === 1 && !isCreatingGroup) ? [{
                            userId: usersToCreateChat[0].matrixId,
                            displayName: `${usersToCreateChat[0].firstName} ${usersToCreateChat[0].lastName}`,
                            avatarUrl: usersToCreateChat[0].avatarUrl
                        }] : roomUsers}
                        roomAvatarUsers={roomAvatarUsers}
                        users={users}
                        usersIds={usersIds}
                        selectedRoomId={selectedRoomId}
                        onClose={() => {
                            setProfileUserIdToView(null);
                            setShowInfoPanel(false);
                        }}
                        currentUserId={currentUserId || ''}
                        inviteUserToRoom={inviteUserToRoom}
                        usersWithStatuses={infoPanelUsersWithStatuses}
                        onKickMember={handleKickMember}
                        onChangeGroupName={async (name: string) => {
                            if (!selectedRoomId) throw new Error('No room selected');
                            await changeRoomName(selectedRoomId, name);
                        }}
                        onChangeRoomAvatar={async (file: File) => {
                            if (!selectedRoomId) throw new Error('No room selected');
                            await changeRoomAvatar(selectedRoomId, file);
                        }}
                        userWithoutConversation={(prepareNewChat && usersToCreateChat.length === 1 && !isCreatingGroup) ? {
                            userId: String(usersToCreateChat[0].userId || usersToCreateChat[0].id),
                            firstName: usersToCreateChat[0].firstName,
                            lastName: usersToCreateChat[0].lastName,
                            position: usersToCreateChat[0].position || '',
                            email: usersToCreateChat[0].email,
                            missedCallEmailNotificationEnabled: false,
                            groups: [],
                            msisdns: [],
                            avatarUrl: usersToCreateChat[0].avatarUrl || '',
                            userTimeZone: usersToCreateChat[0].userTimeZone || ''
                        } : userWithoutConversation?.user}
                        getUserId={getUserId}
                        profileUserId={profileUserIdToView}
                        onProfileClose={() => setProfileUserIdToView(null)}
                        isMuted={isMuted}
                        onMute={async (durationHours) => {
                            try {
                                await muteRoom(durationHours);
                                const duration = durationHours === null
                                    ? 'until you turn them back on'
                                    : `for ${durationHours} ${durationHours === 1 ? 'hour' : 'hours'}`;
                                dispatch(showToast({
                                    title: 'Notifications muted',
                                    description: `You won't receive notifications from this conversation ${duration}`,
                                    type: 'success'
                                }));
                            } catch (error) {
                                console.error('Failed to mute room:', error);
                                dispatch(showToast({
                                    title: 'Error',
                                    description: 'Failed to update notification settings',
                                    type: 'error'
                                }));
                            }
                        }}
                        onUnmute={async () => {
                            try {
                                await unmuteRoom();
                                dispatch(showToast({
                                    title: 'Notifications enabled',
                                    description: 'You will receive notifications from this conversation',
                                    type: 'success'
                                }));
                            } catch (error) {
                                console.error('Failed to unmute room:', error);
                                dispatch(showToast({
                                    title: 'Error',
                                    description: 'Failed to update notification settings',
                                    type: 'error'
                                }));
                            }
                        }}
                        onLeaveRoom={async () => {
                            try {
                                await leaveRoom();
                                dispatch(showToast({
                                    title: 'Left conversation',
                                    description: 'You have left the conversation',
                                    type: 'success'
                                }));
                            } catch (error) {
                                console.error('Failed to leave room:', error);
                                dispatch(showToast({
                                    title: 'Error',
                                    description: 'Failed to leave conversation',
                                    type: 'error'
                                }));
                            }
                        }}
                        onLeaveDirectRoom={() => leaveRoom({forget: true})}
                        onSelectRoom={selectRoom}
                        isAiBotInRoom={isAiBotInRoom}
                        isAiBotEnabled={isAiBotEnabled ?? false}
                        isEncrypted={selectedRoomId ? isRoomEncrypted(selectedRoomId) : false}
                        onEnableEncryption={selectedRoomId ? async () => {
                            try {
                                await enableRoomEncryption(selectedRoomId);
                                dispatch(showToast({
                                    title: 'Encryption enabled',
                                    description: 'This conversation is now end-to-end encrypted',
                                    type: 'success'
                                }));
                            } catch (error) {
                                console.error('Failed to enable encryption:', error);
                                dispatch(showToast({
                                    title: 'Error',
                                    description: 'Failed to enable encryption',
                                    type: 'error'
                                }));
                            }
                        } : undefined}
                    />
                </div>
            )}

            {/* Knowledge Base Panel - positioned absolutely to slide in from the right */}
            {showKnowledgeBasePanel && isAiKnowledgeBaseEnabled && (
                <div className="absolute right-0 top-0 h-full z-40 transition-all duration-300 ease-in-out w-80 opacity-100">
                    <KnowledgeBasePanel
                        onClose={() => setShowKnowledgeBasePanel(false)}
                        tenant={tenant ?? undefined}
                    />
                </div>
            )}

            {showMediaUpload && selectedRoomId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <MediaUpload
                        onUploadComplete={() => setShowMediaUpload(false)}
                        onClose={() => setShowMediaUpload(false)}
                        onFileSelect={handleFileUpload}
                    />
                </div>
            )}
            {showCreateRoom &&
                <CreateRoomModal startPreparingChat={startPreparingChat} onRoomCreate={handleCreateRoom} users={users}
                                 onClose={() => setShowCreateRoom(false)}
                />}

            <DuplicateGroupDialog
                open={duplicateGroupRoomId !== null}
                named={duplicateGroupIsNamed}
                onViewGroup={() => {
                    const roomId = duplicateGroupRoomId;
                    setDuplicateGroupRoomId(null);
                    if (roomId) void selectRoom(roomId);
                }}
                onCancel={() => setDuplicateGroupRoomId(null)}
            />

            <InviteProgressDialog
                open={inviteProgress !== null}
                invited={inviteProgress?.invited ?? 0}
                total={inviteProgress?.total ?? 0}
                error={inviteProgress?.error}
                onClose={() => setInviteProgress(null)}
            />

            {/* Image Gallery - Rendered at top level */}
            {showImageGallery && (
                <ImageGallery
                    images={galleryImages}
                    initialIndex={galleryStartIndex}
                    onClose={() => setShowImageGallery(false)}
                />
            )}

        </div>
    );
};

export default Chat;
